/**
 * The allocation map, as an SVG string — shared by the browser and the
 * OpenGraph rasterizer.
 *
 * Layout follows the research tool's grid, and the orientation is not
 * arbitrary — **roles are rows, layers are columns**. Role counts are bounded
 * (a dozen or so, whatever the architecture); layer counts are not, and grow
 * to hundreds on frontier models. Putting the unbounded axis horizontally
 * makes the grid scroll in the direction there is room to scroll.
 *
 * **A ragged right edge is a finding, not a rendering bug.** When `attn.k` and
 * `attn.v` stop partway along while `attn.q` runs the full depth, that is a
 * real architectural boundary — grouped-query attention, sliding-window layers,
 * a partially-quantized stack. Missing cells are therefore *absent*, never
 * drawn as empty boxes, because an empty box reads as "zero" and a gap reads as
 * "not present". Those are different claims.
 *
 * Colour is a **single hue, light→dark** (inverted in dark mode, see the ramp
 * tokens) because this encodes magnitude. Never a rainbow, never a diverging
 * pair — there is no meaningful midpoint in "bits per weight".
 *
 * ## Why this module is environment-free
 *
 * Plan §23 puts a second consumer behind the same layout: an og-image Lambda
 * that reads the same Hub metadata, renders this SVG and rasterizes it with
 * resvg. That is what makes the share card **trustworthy by construction** —
 * we computed it from Hub metadata, nobody asserted it — and it only holds if
 * there is exactly one renderer. So nothing here may touch `document`,
 * `window`, a `CSSStyleDeclaration`, or a timer, and nothing here may name a
 * colour or a font: both arrive resolved, in a `MapPalette`. See
 * `palette.ts` for why that indirection is not optional.
 *
 * Two consequences of "no browser" show up as rules in the code below:
 *
 * 1. **No `var(--…)` may reach card output.** A rasterizer has no stylesheet,
 *    so a leaked custom property paints nothing at all — and the browser keeps
 *    working, so the only symptom is a broken PNG. `card-map.test.ts` asserts
 *    the absence directly.
 * 2. **No layout decision may depend on measured text.** There is no text
 *    metrics API here and the rasterizer's fonts are not ours. Where a width is
 *    genuinely needed — the row-label gutter, truncating a long model id — it
 *    comes from `MONO_ADVANCE_EM`, a documented approximation that is rounded
 *    in the safe direction.
 *
 * The interactive mode keeps the hover targets, `data-` attributes and ids the
 * app wires up in `ui/anatomy-map.ts`; card mode emits none of them, because a
 * card has no pointer and an id in an embedded SVG collides with the page's.
 */

import type { AnatomyMarginals, Marginal } from "../analysis/anatomy-marginals.js";
import type { Anatomy, AnatomyCell } from "../analysis/anatomy.js";
import { cellKey } from "../analysis/anatomy.js";
// `ui/format.ts` is pure number formatting with no DOM reference, so importing
// it from here does not compromise the environment-free contract. Re-stating
// "7.24B" and "4.51" formatting rules in a second place would.
import { formatBytes, formatParams } from "../ui/format.js";
import type { MapPalette } from "./palette.js";

/* -- interactive geometry -------------------------------------------------- */

const CELL = 30;
const GAP = 4;
const AXIS_HEIGHT = 34;

/**
 * Half a gap of slack at the leading edge of every band.
 *
 * An SVG clips to its own viewport, so a cell drawn flush at `x = 0` loses the
 * outer half of its 2px focus ring — and the top row loses it to the same edge.
 * Spending half the inter-cell gap on the leading edge buys the ring back
 * without changing any band's width or height: the trailing edge already has a
 * full gap of slack after the last cell.
 */
const EDGE = GAP / 2;

/** Row labels; the tick strip and the pinned column's heading. */
const ROW_LABEL_FONT = 11;
const AXIS_FONT = 10;

/**
 * The value printed inside each cell, in interactive mode.
 *
 * **Why the map prints numbers at all.** Colour answers "which cells differ";
 * it does not answer "by how much", and the only place that answer existed was
 * the detail panel below the grid — which costs a pointer trip per cell and
 * makes comparing two cells a matter of remembering the first number while
 * aiming at the second. With the number in the cell the whole grid is readable
 * at rest.
 *
 * Same size as the row labels, which is what was asked for and what fits: at
 * 11px the monospace advance is 11 × 0.6 = 6.6px, so four characters is 26.4px
 * inside a 30px cell — 1.8px of clearance each side. Five characters is 33px
 * and would overrun the cell and the gap after it, which is why every formatter
 * below is capped at four and why the dtype map declines the annotation
 * entirely when a dtype name is longer than that.
 *
 * **Card mode never gets this.** Plan §23 rules that the share card has no cell
 * annotations — at 1200×630 in a timeline the grid is read as a shape, and text
 * at that scale is noise that also multiplies the font-substitution risk. Card
 * output is byte-compared against a golden PNG, so this staying on the
 * interactive path is a hard requirement rather than a preference.
 */
const CELL_LABEL_FONT = ROW_LABEL_FONT;

/**
 * Compare mode: two values in one 30px cell, A above-left and B below-right.
 *
 * **What the fill encodes when there are two values: the difference, A minus B**
 * (owner 2026-08-12; direction ruled by the reviewer the same day).
 *
 * This file argued the opposite for months — that the fill must stay A's own
 * value — in three parts. Two of them were right and still hold; the third was
 * wrong, and it is worth keeping all three written down because the two that
 * survived are what shape the implementation.
 *
 *  - **Still true: a difference is not defined everywhere.** A cell can exist
 *    in A and not in B; the ragged edge is a *finding*. Such a cell is drawn as
 *    bare plane, never as a colour — painting it neutral would assert the two
 *    models agree there, which is the confident-wrong-number failure this file
 *    refuses at every other opportunity.
 *
 *    There is a second way to be undefined, and it was missed for six days:
 *    both cells exist and *neither has the quantity*. A buffer has no bits per
 *    weight, so `0 - 0` reached the neutral step and painted agreement about a
 *    number neither model holds — under an em dash saying exactly that. Same
 *    resolution, same plane; see `paintFor`.
 *  - **Still true: `dtype` is categorical.** "BF16 minus U8" is not a quantity,
 *    so that one metric keeps A's categorical colouring and the legend says so.
 *    `diffSpan` returns `undefined` and the sequential path is untouched.
 *  - **Wrong: that one grid may only ever carry one colour system.** With A's
 *    value on the fill, *the picture was identical whichever model B was* — the
 *    entire comparison lived in two 9px numbers, which is colour encoding
 *    nothing while looking restrained. A signed quantity with a meaningful zero
 *    is the case a diverging scale exists for.
 *
 * The direction is **A − B**: the grid's shape, its rows, its labels and every
 * hovered cell are A's, so the fill answers a question about the cell the
 * cursor is on — does this tensor spend more or less than its counterpart —
 * rather than stating something about the absent model projected onto A's
 * square. On the path the provenance suggestion creates, where a quantization
 * is A and its parent is B, "what did quantizing cost me here" is then
 * negative, cool, and needs no translating.
 *
 * **The size is arithmetic, not taste.** Two values at the solo map's 11px
 * would be 26.4px each and could only be stacked; at 9px a four-character value
 * is 4 × 9 × 0.6 = 21.6px inside the 26px a cell leaves after its 2px insets,
 * which is what lets the two be *offset* — A anchored to the left inset, B to
 * the right — instead of merely stacked. The slant is the separator: it reads
 * as the "A / B" the owner asked for without spending a character on the slash.
 *
 * **The offset is a slant, not a separation, and that is a real limit.** Two
 * four-character values in genuinely opposite corners would need 2 × 21.6 =
 * 43.2px of width in a 30px cell, i.e. about 5px type. So they overlap
 * horizontally by ~17px and are told apart by their baselines, 8.7px of clear
 * space apart. Legibility beat completeness: the alternative was two characters
 * each, which cannot distinguish 4.5 from 4.8 bits per weight.
 */
const COMPARE_LABEL_FONT = 9;

/** Clearance at each end of a compare label, inside the cell. */
const COMPARE_LABEL_INSET = 2;

/**
 * The caption that names the scrollable axis, and the column that is not on it.
 *
 * The non-layer column collects every tensor with no block index — the
 * embedding table, the head, the final norm — the parts shared by the whole
 * stack rather than repeated per layer. It used to be labelled `·`, which reads
 * as a rendering artifact rather than as a column heading.
 */
const AXIS_CAPTION = "layer →";
const GLOBAL_LABEL = "global";

/**
 * The card's per-axis pitch ceiling.
 *
 * Applied to each axis independently (spec §12.3), which is what stops a
 * shallow model from stretching a handful of columns across the whole card
 * while still letting a 150-layer one shrink to heat-map pixels.
 */
const MAX_PITCH = 34;

/**
 * Advance width of one monospace glyph, in ems.
 *
 * Every monospace face in the `--font-mono` stack, and every substitute a
 * Linux-based rasterizer is likely to fall back to (DejaVu Sans Mono, Liberation
 * Mono, Noto Sans Mono), sits between 0.60 and 0.62 em. 0.60 is the low end, and
 * every layout below rounds *up* from it, so the error budget is spent on
 * whitespace rather than on a clipped label. Never replace this with a measured
 * width: there is nothing to measure it with in a Lambda.
 */
const MONO_ADVANCE_EM = 0.6;

/**
 * The same idea for the proportional face, and a rougher one by nature.
 *
 * A sans advance genuinely varies per glyph — `i` and `W` are nowhere near each
 * other — so this is an average, used only where being a few pixels out changes
 * nothing that can be noticed: the gap inside the logo lockup, and the indent of
 * the tower's detail line. Anything whose *edge* must land exactly is anchored
 * with `text-anchor` instead, which the rasterizer resolves from real metrics.
 */
const SANS_ADVANCE_EM = 0.55;

/**
 * Characters a cell label may carry: `floor(30 ÷ (11 × 0.6))` = 4.
 *
 * Derived rather than written down, so a change to the cell size or the label
 * font moves the cap with it instead of silently overrunning. Rounded *down*,
 * which is the safe direction here: the cost of an over-strict cap is a coarser
 * number, and the cost of a loose one is text spilling across the gap into the
 * next cell.
 */
const CELL_LABEL_MAX_CHARS = Math.floor(CELL / (CELL_LABEL_FONT * MONO_ADVANCE_EM));

/**
 * Cap height as a fraction of font size, for vertically centring a label.
 *
 * Baseline-shifting by half the cap height centres text in a box the way an eye
 * expects. Used instead of `dominant-baseline`, whose support in SVG rasterizers
 * is uneven enough that the browser and the PNG would disagree.
 */
const CAP_HEIGHT_EM = 0.7;

/**
 * Characters a *compare* label may carry: `floor(26 ÷ (9 × 0.6))` = 4.
 *
 * Derived from the compare font and the insets for the same reason
 * `CELL_LABEL_MAX_CHARS` is derived from the solo one. It lands on 4 as well,
 * which is the happy accident that lets both modes share one set of
 * four-character formatters — but it is checked rather than assumed, because a
 * change to either constant would silently move it.
 */
const COMPARE_LABEL_MAX_CHARS = Math.floor(
  (CELL - COMPARE_LABEL_INSET * 2) / (COMPARE_LABEL_FONT * MONO_ADVANCE_EM),
);

/** Cap height of a compare label, which both baselines below are centred on. */
const COMPARE_CAP = COMPARE_LABEL_FONT * CAP_HEIGHT_EM;

/**
 * The two baselines, each centring its label's cap box in half the cell.
 *
 * At 9px that is `y + 10.65` and `y + 25.65`: 4.35px of clearance at the top of
 * the cell, 8.7px between the two cap boxes, and 4.35px below — symmetric, and
 * the middle gap is twice either margin, which is what makes the pair read as
 * two values rather than as one wrapped one. Digits and the em dash have no
 * descenders, so the lower label's bottom clearance is real.
 */
const COMPARE_A_BASELINE = CELL / 4 + COMPARE_CAP / 2;
const COMPARE_B_BASELINE = (CELL * 3) / 4 + COMPARE_CAP / 2;

/**
 * What a compare cell prints where one side has no number.
 *
 * An em dash, which is this project's mark for "not defined here" everywhere
 * else a statistic is absent (see `ui/compare-view.ts`). It covers two
 * different claims — B has no such cell at all, and B has the cell but its
 * quantization scheme could not be pinned down — because one character cannot
 * distinguish them and inventing a second glyph for a distinction nobody would
 * decode is worse than one honest blank. The cell's `aria-label` and the detail
 * panel below the grid both say which it is, in words.
 */
const NO_VALUE = "—";

export type AnatomyMetric = "dtype" | "bpw" | "bytes" | "params";

/**
 * Card mode drops hover affordances, ids, and per-cell annotations; interactive
 * mode keeps them because the app wires listeners to them.
 */
export type MapMode = "interactive" | "card";

/**
 * One vertical slice of the interactive map, rendered as its own `<svg>`.
 *
 * **Why the interactive map is three documents and the card is one.** The card
 * has a fixed frame and fits the whole grid into it. The app does not: the layer
 * axis is unbounded, so the grid has to scroll — and when it was a single SVG
 * inside a single scroller, scrolling right took the row labels with it and left
 * the reader with an unlabelled field of squares. Worse, the non-layer column is
 * *last*, so on any model deep enough to overflow, the embedding — routinely a
 * quarter of the file's bytes — sat past the right edge and its row rendered
 * visually empty. A map that shows an empty `embed` row is not a layout defect;
 * it is the app misdescribing the model.
 *
 * So the two axes that must never scroll are lifted out of the scroller:
 * `labels` pins left, `global` pins right, and only `layers` — the one axis that
 * is genuinely unbounded — moves. Each band is laid out by
 * `interactiveMapLayout` and rendered by the same code path, so a row is at the
 * same `y` in all three by construction rather than by two agreeing constants.
 */
export type MapBand = "labels" | "layers" | "global";

export interface MapRenderOptions {
  readonly anatomy: Anatomy;
  readonly metric: AnatomyMetric;
  readonly width: number;
  readonly height: number;
  /** Resolved colour values, because a Lambda has no CSS custom properties. */
  readonly palette: MapPalette;
  /** Card mode drops hover affordances, ids, and per-cell annotations. */
  readonly mode: MapMode;
  /**
   * Which slice to draw, in interactive mode. Ignored by the card, which has a
   * frame to fit and therefore no scroller to pin anything outside of.
   */
  readonly band?: MapBand;
  /**
   * Row and column totals to print on the axes — `analysis/anatomy-marginals.ts`.
   *
   * **Optional, and the card never passes it.** This renderer has two consumers
   * (the browser and the og-image Lambda) and the share card is a published
   * artifact governed by OG-SPEC; adding numbers to it is a separate decision
   * with its own review. Absent means byte-identical output to before, which the
   * card golden verifies — that is what makes this addition safe to make in a
   * shared file (owner's ruling, 2026-08-14).
   */
  readonly marginals?: AnatomyMarginals;
  /**
   * Card heading — the model id. Ignored in interactive mode, where the
   * surrounding card chrome already names the repo.
   */
  readonly title?: string;
  /**
   * Card sub-heading. Defaults to the params + effective-bpw headline plan §23
   * asks for; pass a string only to override it.
   */
  readonly subtitle?: string;
  /**
   * The model's stacks, with the one the map draws marked — §12.1 and §12.4.
   *
   * Sits between the model id and the statistics, because it governs both: the
   * grid below is this tower, and so is every number under it. Naming the
   * selected stack in bold is what lets the statistics be tower-scoped without
   * misleading anybody, which is the alternative §12.4 offers to
   * whole-checkpoint numbers.
   *
   * Omitted for a single-stack model. There is nothing to disambiguate, the
   * numbers are the whole checkpoint by definition, and a lone "Text" would be
   * a label for a distinction that does not exist.
   */
  readonly towerLine?: ReadonlyArray<{
    readonly label: string;
    readonly selected: boolean;
  }>;
  /**
   * Which model in the repo the map is of — `Q4_K_M`, `vae`.
   *
   * Sits above the tower line, because it is the coarser choice: a part is a
   * different *file*, a tower is a stack inside one. Reading downward the card
   * narrows — repo, part, tower, statistics — and each row governs everything
   * below it.
   *
   * **Not optional decoration.** `unsloth/Qwen3.5-4B-GGUF` offers 22
   * quantizations spanning 2.89 to 8.53 bits per weight, and a map of one of
   * them titled with only the repo id is unattributable — the argument
   * `renderPart` makes on the page, where it is *less* acute because the page
   * has chips and a URL bar. A card has neither and is a screenshot of the map.
   *
   * Omitted for a repo holding one model, where there is nothing to
   * disambiguate.
   */
  readonly partLine?: string;
  /**
   * What the choice was between, on a repo-level default card — `24
   * quantizations · IQ2_XXS → BF16 · 8.39 GiB – 50.9 GiB`.
   *
   * Sits directly under the repo id and *above* `partLine`, because it is the
   * coarsest row: the card narrows downward — repo, choice space, part, tower,
   * statistics — and each row governs everything below it.
   *
   * This row is what licenses the one below it. A map of `Q4_K_M` under a bare
   * repo id would claim the repo *is* that variant; with this line the card
   * states the enclosing scope, names the variant drawn, and depicts a shape
   * that is true for all of them. §12.4 permits a scope provided it is named,
   * and naming here is load-bearing rather than decorative.
   *
   * Omitted whenever the visitor chose the part themselves, where there is no
   * scope to disclose.
   */
  readonly repoLine?: string;
  /**
   * What this stack is, when it is not a whole model — §12.5's amendment.
   *
   * When present the grid is not drawn and this takes its place as the card's
   * dominant element. A one-column grid is not a map: at feed size it reads as a
   * rendering failure rather than as a fact, which is the argument §12.2 already
   * makes for sending a blank-celled model to `bytes`.
   *
   * The statistics beside it are unchanged — the same `anatomyStatsLine` a map
   * card carries — so a visitor arriving from this card reads the figures they
   * just saw.
   *
   * Card mode only. The page has room for the map *and* the sentence, and shows
   * both; a card at 1200x630 read in a feed has room for one thing said well.
   */
  readonly insteadOfMap?: string;
  /**
   * The second model's grid, in compare mode. **Interactive only.**
   *
   * The drawn grid stays `anatomy`'s — A's — and this is looked up cell by cell
   * to print B's value beside A's. That asymmetry is deliberate: a cell A does
   * not have is not drawn (the ragged edge is a finding), and a cell B does not
   * have prints an em dash, so the grid's *shape* keeps meaning exactly what it
   * means on a solo map. `ui/anatomy-map.ts` counts what B has that A does not
   * and states it, so nothing is silently dropped.
   *
   * **Costs no Hub requests.** Every metric this map draws — storage dtype,
   * bits per weight, bytes, parameters — comes from safetensors headers already
   * read for B's inventory, plus B's `config.json`, which compare mode fetched
   * when it loaded B. An A-against-B map of *weights* would mean reading every
   * tensor from both repos, which the no-crawling invariant forbids; this is a
   * different thing that happens to look similar.
   *
   * Card mode ignores it. Plan §23 rules the share card carries no cell
   * annotations at all, its bytes are compared against a golden PNG, and a card
   * has no second repo to compare against anyway.
   */
  readonly compare?: Anatomy;
}

/** Escapes the five characters that would otherwise end an attribute or a tag. */
export function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Fixed-precision number formatting for coordinates.
 *
 * Determinism is a hard requirement, not a preference: plan §23's golden-PNG
 * test compares rasterized bytes, so identical input must produce an identical
 * string. Two decimals is finer than any rasterizer's sub-pixel grid and short
 * enough to keep the document small.
 */
function num(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  // `-0` stringifies as "-0", which would make two mathematically identical
  // layouts produce different bytes.
  return Object.is(rounded, -0) ? "0" : String(rounded);
}

function metricValue(cell: AnatomyCell, metric: AnatomyMetric): number {
  if (metric === "bytes") return cell.bytes;
  if (metric === "params") return cell.params;
  return cell.effectiveBpw;
}

export function metricLabel(metric: AnatomyMetric, mode: Anatomy["mode"]): string {
  if (metric === "dtype") return "storage dtype";
  if (metric === "bytes") return "bytes";
  if (metric === "params") return "parameters";
  return mode === "bpw"
    ? "effective bits per weight"
    : "bits per weight (from storage dtype)";
}

/**
 * Every storage dtype in the grid, in first-seen order.
 *
 * First-seen rather than sorted because the categorical colour assignment below
 * keys off this order, and a dtype must keep its colour as the view changes.
 */
export function distinctDtypes(anatomy: Anatomy): readonly string[] {
  const seen: string[] = [];
  for (const cell of anatomy.cells.values()) {
    for (const dtype of cell.dtypes) if (!seen.includes(dtype)) seen.push(dtype);
  }
  return seen;
}

/**
 * The metric a repo should open on: **whichever one has something to show.**
 *
 * A quantized repo opens on effective bpw — that is the decision the map exists
 * to show, and it varies by construction.
 *
 * An unquantized repo has no bpw structure at all: bits-per-weight is a function
 * of the storage dtype, so a single-dtype model is a grid of identical squares
 * whichever of `bpw` or `dtype` is picked. That grid is the largest thing on the
 * page and it carries exactly one bit of information — which the legend can
 * state in a sentence, and now does (see `legendModel`). So a repo with one
 * dtype opens on **bytes**, which is uniform for no model: it shows immediately
 * that the embedding and the MLP carry the mass and that attention does not.
 *
 * A *mixed*-dtype repo opens on **bpw when the bits-per-weight actually vary**,
 * which is the case `mode` alone gets wrong. `mode` asks "did a folder resolve a
 * quantization scheme?", and that is a safetensors-shaped question: GGUF cells
 * carry no `quantLabel` because the **dtype is the scheme**, so `mode` says
 * "dtype" for a file whose every cell has a real, varying cost. Measured on
 * `unsloth/Qwen3.5-4B-GGUF` at Q4_K_M — 282 of 282 cells, 4.50 to 32.00, the
 * true ggml block widths.
 *
 * And for GGUF it is the *most* informative view rather than merely a valid
 * one. A filename states a size target, not the formats inside — `…-Q2_K.gguf`
 * contains no `Q2_K` tensor at all, because llama.cpp reaches a target by
 * mixing formats per tensor role (see `labelFor` in `gguf/variants.ts`). The
 * bpw map is what shows that mixture; the dtype map names the formats without
 * ordering them, and the bytes map mostly redraws the architecture.
 *
 * The rule is deliberately about the *values* rather than about the format.
 * `inventory.ts` keeps every consumer from learning that GGUF exists, and a
 * branch on the format here would be the first place that broke.
 *
 * "The grid is uniform" is itself informative, so it is not discarded: it is
 * moved from 200 identical squares into one line of legend text, and the map
 * spends its area on a question that has an answer. Every metric stays offered
 * as a chip either way — this picks the opening view, not the only view.
 */
export function defaultMetric(anatomy: Anatomy): AnatomyMetric {
  if (anatomy.mode === "bpw") return "bpw";
  const dtypes = distinctDtypes(anatomy).length;
  if (dtypes > NAMEABLE_DTYPES && bpwVaries(anatomy)) return "bpw";
  return dtypes > 1 ? "dtype" : "bytes";
}

/**
 * How many dtypes a categorical map can still be read by *name*.
 *
 * Two is a partition: `U8` beside `BF16` says packed beside not-packed, and the
 * names carry the meaning the ramp would throw away. Beyond that it is a
 * spectrum — five ggml formats have an obvious ordering by cost, and a
 * categorical palette is precisely the encoding that loses it.
 *
 * So the threshold is about legibility, not about format. It happens to send
 * GGUF to the ramp and leave the BF16-beside-F32 finding on the dtype map,
 * which is the behaviour each was already getting for its own good reason.
 */
const NAMEABLE_DTYPES = 2;

/**
 * Does every cell that *can* have a bits-per-weight have one, and do they differ?
 *
 * Both halves matter. A cell whose value could not be worked out cannot be
 * placed on the ramp, so a single unresolved cell would leave a hole in the
 * opening view; and a grid where they are all equal is the
 * 200-identical-squares case this function exists to avoid, whatever the dtypes
 * underneath say.
 *
 * ## The buffer is skipped, not counted
 *
 * This tested `!(cell.effectiveBpw > 0)` and bailed — the `value <= 0` sentinel
 * again, in the *selection* logic rather than the formatting logic, which is why
 * #103 did not reach it. A buffer's zero is not a hole: it is a cell with no
 * such quantity, which the map now draws off-scale under an em dash quite
 * legibly. Reading it as a hole **suppressed the bpw view entirely** for any
 * model carrying a causal mask — including the mixed-precision quantized files
 * whose whole story is that their weights span 4 to 16 bits.
 *
 * Skipping is load-bearing and is not the same as counting the zero. If a
 * buffer's non-applicable bpw were folded in as a real `0`, a flat 32-bit
 * checkpoint would read `min = 0, max = 32`, "vary", and open on a ramp that
 * has one weight value on it — `openai-community/gpt2` is exactly that shape and
 * is the owner's named boundary (2026-08-19). Excluding the cell makes its
 * weights `max === min` and returns `false`, which is the behaviour it has
 * today and must keep.
 */
export function bpwVaries(anatomy: Anatomy): boolean {
  let min = Number.POSITIVE_INFINITY;
  let max = 0;
  let weights = 0;
  for (const cell of anatomy.cells.values()) {
    const kind = cellKind(cell, "bpw");
    // No such quantity — not a hole. Contributes nothing in either direction.
    if (kind === "not-applicable") continue;
    // A genuine refusal. This is the hole the original test was built for, and
    // it still bails: a ramp with an uncoloured cell is the case that made the
    // opening view a bad default.
    if (kind === "unknown") return false;
    weights += 1;
    min = Math.min(min, cell.effectiveBpw);
    max = Math.max(max, cell.effectiveBpw);
  }
  // All buffers and nothing else: no weights, so nothing to put on the ramp.
  return weights > 0 && max > min;
}

function formatMetric(value: number, metric: AnatomyMetric): string {
  if (metric === "bytes") return formatBytes(value);
  if (metric === "params") return formatParams(value);
  return value.toFixed(2);
}

/* -- cell labels ----------------------------------------------------------- */

/**
 * A magnitude in at most four characters, unit letter included.
 *
 * Not `formatBytes`/`formatParams`: those are the long forms this project uses
 * everywhere a number is read on its own — "54.2 MiB", "7.24B" — and they are
 * five to eight characters, which does not fit in a 30px cell and would render
 * as a smear rather than as a number. The long forms still carry the legend
 * endpoints, the detail panel and every cell's `aria-label`, so full precision
 * and the unabbreviated unit are always one hover or one focus away; what the
 * cell needs is the *order of magnitude and leading digits*, at a glance,
 * across a whole grid.
 *
 * The two thresholds are both about the character budget rather than about
 * taste, and both are one step tighter than the obvious value:
 *
 *  - **999.5, not `base`** — the unit is chosen before the value is rounded, so
 *    1023.6 KiB would pick "K" and then print `1024K`, five characters.
 *  - **9.95, not 10** — `toFixed(1)` on 9.96 is `"10.0"`, which with a suffix is
 *    five characters again.
 */
function compactMagnitude(
  value: number,
  units: readonly string[],
  base: number,
): string {
  let scaled = value;
  let unit = 0;
  while (scaled >= 999.5 && unit < units.length - 1) {
    scaled /= base;
    unit += 1;
  }
  const suffix = units[unit] ?? "";
  // The unscaled unit is a count of whole things — bytes, or parameters — so a
  // decimal point there would be spending a character on a digit that does not
  // exist.
  return unit === 0 || scaled >= 9.95
    ? `${Math.round(scaled)}${suffix}`
    : `${scaled.toFixed(1)}${suffix}`;
}

/**
 * What a metric's value *means* for a cell, rather than what number it is.
 *
 * ## The sentinel this replaces
 *
 * Both formatters used to test `value <= 0` and infer intent from it. That test
 * carries three different claims at once:
 *
 * - **not a weight** — a non-parameter buffer. Its `params` really is zero and
 *   its bits-per-weight does not exist, because bits *per weight* needs weights.
 * - **not resolvable** — a quantized cell whose scheme could not be pinned down.
 *   `anatomy.ts` refuses to guess there because the wrong guess is out by a
 *   factor of two, and a blank is that refusal made visible.
 * - **genuinely zero** — an ordinary measurement that happens to be nothing.
 *
 * Overloading one test with three claims had already produced two answers for
 * one row: on `openai-community/gpt2` the `params` view's margin read `0` while
 * the same buffer row's cells read blank — margin saying "zero", cells saying
 * "unknown". Reported by a visitor as `94e8b5db`, and the divergence is the
 * argument for a classifier rather than for fixing both call sites in lockstep:
 * they were already out of step.
 *
 * Fourth time a buffer has been caught by a path built for ambiguity, after the
 * parameter count, the byte identity and the marginals' coverage marker.
 */
export type CellValueKind =
  /** A real measurement. Format it. */
  | "value"
  /** Real, and zero. `0` is the answer, not an absence. */
  | "zero"
  /** No such quantity for this cell — bits per weight where there are no
   *  weights. The codebase's word for this is an em dash. */
  | "not-applicable"
  /** We could not work it out and refuse to guess. Stays blank, because a blank
   *  inside a labelled grid is how that refusal reads. */
  | "unknown";

/** The em dash this file already uses for an absent value. See `NO_PART`. */
const NOT_APPLICABLE = "—";

/**
 * The classifier, for callers that decide *which map to draw* rather than what
 * to print in a cell.
 *
 * Exported because the same overloaded sentinel this type exists to retire had
 * two more copies in the **selection** logic — `bpwVaries` below and
 * `cardMetric` in `og/src/model-card.ts` — where `effectiveBpw <= 0` was
 * reading a buffer as a hole and hiding the bits-per-weight view of models that
 * have a perfectly good one. A selection rule and a formatting rule must ask the
 * same question or they disagree about the same cell, which is how the defect
 * survived #103 in the first place.
 *
 * Takes the metric rather than a value, because a caller choosing a view has no
 * value in hand yet — it is asking whether one exists.
 */
export function cellKind(cell: AnatomyCell, metric: AnatomyMetric): CellValueKind {
  return cellValueKind(cell, metric, metricValue(cell, metric));
}

/**
 * Classify one cell's value for one metric.
 *
 * Order matters. A buffer is asked about first because it is the only case that
 * can be *both* zero-valued and fully understood; everything after it treats a
 * non-positive number as a failure to measure, which is what it is once buffers
 * are out of the way.
 */
function cellValueKind(
  cell: AnatomyCell,
  metric: AnatomyMetric,
  value: number,
): CellValueKind {
  if (cell.isBuffer) {
    // Its bytes are real and known; its parameters are really zero; its
    // bits-per-weight is a quantity that does not exist for it.
    if (metric === "bytes") return value > 0 ? "value" : "zero";
    if (metric === "params") return "zero";
    return "not-applicable";
  }
  if (!Number.isFinite(value) || value < 0) return "unknown";
  /*
   * Zero on a non-buffer cell is the ambiguity refusal, not a measurement.
   * `anatomy.ts` sets `params = 0` exactly where it declines to guess, so this
   * must stay `unknown` — turning it into `"—"` or `"0"` would convert a
   * refusal into a confident claim, which is the one way this change goes
   * wrong.
   */
  if (value === 0) return "unknown";
  return "value";
}

/**
 * The value to print inside a cell, or `undefined` for a cell that has none.
 *
 * **`undefined` rather than `"0.0"` for an unresolved cell.** A quantized tensor
 * whose scheme could not be pinned down reports `params = 0` and therefore
 * `effectiveBpw = 0` — `anatomy.ts` refuses to guess there on purpose, because
 * the wrong guess is wrong by a factor of two. Printing the zero would undo
 * that refusal by putting a confident number on the exact cells the ambiguity
 * notice is about. An unlabelled cell inside a labelled grid reads as "not
 * known", which is the claim being made.
 */
function cellLabel(cell: AnatomyCell, metric: AnatomyMetric): string | undefined {
  if (metric === "dtype") return cell.dtypes[0];
  const value = metricValue(cell, metric);
  const kind = cellValueKind(cell, metric, value);
  if (kind === "unknown") return undefined;
  if (kind === "not-applicable") return NOT_APPLICABLE;
  if (kind === "zero") return "0";
  if (metric === "bytes")
    return compactMagnitude(value, ["B", "K", "M", "G", "T"], 1024);
  if (metric === "params") return compactMagnitude(value, ["", "K", "M", "B"], 1000);
  // Bits per weight lives in a narrow band (2–32) and needs no unit: "32" is
  // what the reader is looking for, and one decimal is the resolution that
  // separates 4.50 from 5.00 — the difference a group size makes.
  return value >= 9.95 ? String(Math.round(value)) : value.toFixed(1);
}

/**
 * Which paint treatment a cell gets, for tests.
 *
 * `"plane"` is the off-scale neutral this grid uses for "nothing here";
 * `"ramp"` is a real position on the value scale; `"diverge"` is a signed
 * position on compare mode's A−B scale, **including its neutral midpoint**.
 * Asserting the *treatment* rather than a hex value keeps the test about the
 * decision rather than about the palette, which is themed and may legitimately
 * change.
 *
 * Passing `compare` selects the compare path, which is a genuinely different
 * branch of `paintFor` rather than the same one with an extra argument — it was
 * returning before the not-applicable check ever ran. So this goes through
 * `paintFor` itself rather than re-deriving the answer from `cellValueKind`:
 * a helper that reimplements the decision it is checking would have agreed with
 * the classifier while the renderer disagreed with both.
 *
 * `compare` is an object holding a possibly-absent `b` rather than an optional
 * cell, because there are **three** states and an optional argument can only
 * say two. Omitted is the single-model grid; `{ b: undefined }` is compare mode
 * with this cell missing from B, which is its own paint rule; `{ b: cell }` is
 * two cells to difference. Collapsing the middle one is how the first draft of
 * this helper came to assert `"ramp"` about a case it had silently left.
 *
 * The palette is the caller's, for the reason this module exists — it may not
 * name a colour, and that holds for a test helper too. It is also what makes
 * the classification possible: `plane` is *whatever this palette calls
 * surface*, compared rather than recognised.
 */
export function paintKindForTest(
  cell: AnatomyCell,
  metric: AnatomyMetric,
  palette: MapPalette,
  compare?: { readonly b: AnatomyCell | undefined },
): "plane" | "ramp" | "diverge" {
  const shading: Shading = {
    metric,
    min: 0,
    max: 32,
    palette,
    dtypeSlots: undefined,
    // Non-zero so the neutral step is reached by a zero *delta* rather than by
    // the degenerate span guard — otherwise this asserts nothing about buffers.
    diffSpan: compare ? 32 : undefined,
  };
  const { fill } = paintFor(cell, shading, compare?.b);
  if (fill === palette.surface) return "plane";
  return compare ? "diverge" : "ramp";
}

/**
 * `cellLabel`, for tests.
 *
 * Exported because the three meanings of zero are the whole of what a reader
 * can be wrong about here, and driving them through a rendered SVG would test
 * the layout rather than the distinction.
 */
export function cellLabelForTest(
  cell: AnatomyCell,
  metric: AnatomyMetric,
): string | undefined {
  return cellLabel(cell, metric);
}

/**
 * Whether this map's labels fit, decided once for the whole grid.
 *
 * All-or-nothing rather than per cell. A grid where some cells carry a number
 * and others do not reads as missing data — the same reason absent cells are
 * drawn absent rather than empty — so the only cells left blank are the ones
 * that genuinely have nothing to say (see `cellLabel`).
 *
 * Only the dtype map can fail this. Its labels are names rather than numbers,
 * and while every dtype a real repo uses fits in four characters (`F32`,
 * `BF16`, `U8`, `I32`), `F8_E4M3` and `F8_E5M2` do not — and they differ in
 * their *last* character, so truncating would print the same `F8_…` for two
 * different types. Where that happens the legend does the work it already does
 * for a categorical scheme: it names every colour, once, in full.
 */
function labelsFit(
  anatomy: Anatomy,
  metric: AnatomyMetric,
  compare: Anatomy | undefined,
): boolean {
  if (metric !== "dtype") return true;
  // In compare mode B's names have to fit too, and against the compare budget:
  // a grid that labelled A and declined B would read as B having no dtype.
  const max = compare ? COMPARE_LABEL_MAX_CHARS : CELL_LABEL_MAX_CHARS;
  const names = compare
    ? [...distinctDtypes(anatomy), ...distinctDtypes(compare)]
    : distinctDtypes(anatomy);
  return names.every((dtype) => dtype.length <= max);
}

/**
 * Whether a metric needs a logarithmic colour scale.
 *
 * Bytes and parameter counts span orders of magnitude within one model — an
 * embedding table is routinely 30× a single attention projection — so a linear
 * ramp spends its whole range separating the one largest cell from everything
 * else, and the entire transformer stack collapses into a single flat shade.
 * Observed immediately on the first real repo: 1.13 KiB to 54 MiB.
 *
 * Effective bits-per-weight is log for a different reason, and this file used to
 * argue the opposite — that bpw "lives in a narrow band where a log scale would
 * flatten exactly the differences the map exists to show". Measured against a
 * real quantized repo, that is backwards twice over.
 *
 * The band is not narrow. `mlx-community/Qwen3.5-4B-OptiQ-4bit` spans 4.50 to
 * 24.00, because a 4-bit body ships alongside norms and embeddings nobody
 * quantized. And log does not flatten the low end — it flattens the *high* one,
 * which here is a handful of 16- and 24-bit cells that only need to read as
 * "much heavier". On that repo the linear ramp put 5.83 and 7.15 on the same
 * shade — 69 cells, a 23% difference in cost, one colour — while leaving four of
 * its nine steps unused on the empty stretch above 8.5. Log separates every
 * distinct value the model actually contains.
 *
 * bpw is a ratio: 4→8 bits is the same event as 16→32, and only a log scale
 * says so. The narrow ranges the old comment worried about are the case where
 * this costs nothing, because log is asymptotically linear there — across a
 * 4.0–4.5 spread the midpoint moves 1.4% of the ramp, well under one step. So
 * the rule does not need to switch on the observed spread: it already reduces to
 * the linear reading exactly where linear was right, and a scale that silently
 * changed rule between two models would make one shade mean two things with
 * nothing on either card to say which.
 */
function isLogScale(metric: AnatomyMetric): boolean {
  return metric === "bytes" || metric === "params" || metric === "bpw";
}

/**
 * Where a value sits on the ramp, as a fraction in [0, 1].
 *
 * **Continuous, not stepped.** Nine discrete steps quantised away the
 * differences the map exists to show: on a GGUF card `Q4_K` at 4.50 and `Q5_K`
 * at 5.50 rounded to the same step — a 22% cost difference in one colour —
 * while five of the nine painted a range nothing occupied. More steps is not
 * the fix; it spreads the same compression more finely. The fraction is what
 * the ramp always computed, and rounding it was the only reason two distinct
 * values could share a fill (owner, 2026-08-10).
 *
 * Relative to the observed range rather than an absolute one: the interesting
 * question is almost always "which parts of *this* model differ from the rest",
 * and an absolute 0–32 bpw scale would render most models flat. The legend
 * states the endpoints so a relative reading is never mistaken for an absolute
 * one, and names the scale when it is logarithmic.
 */
function rampFraction(
  value: number,
  min: number,
  max: number,
  metric: AnatomyMetric,
): number {
  // Everything equal: the midpoint, so a uniform grid is a mid shade rather
  // than an arbitrary end of the ramp.
  if (!(max > min)) return 0.5;
  const fraction = isLogScale(metric)
    ? (Math.log(value) - Math.log(min)) / (Math.log(max) - Math.log(min))
    : (value - min) / (max - min);
  if (!Number.isFinite(fraction)) return 0;
  const clamped = Math.min(1, Math.max(0, fraction));
  // **Bits-per-weight only.** Bytes and parameter counts are log for a
  // different reason — they span orders of magnitude *inside* one model, 1.13
  // KiB to 54 MiB on the first real repo — and plain log already spends the
  // ramp well on them. There is no interesting bottom quarter in a byte map:
  // the small cells are small, and saying so once is enough (owner,
  // 2026-08-10).
  return metric === "bpw" ? clamped ** LOW_END_EMPHASIS : clamped;
}

/**
 * Concave adjustment on the log fraction for **bits-per-weight**, weighting the
 * low end.
 *
 * Log alone makes every doubling an equal span — 4→8 counts the same as 16→32.
 * That is the right *ordering* and the wrong *emphasis*: the owner's rule is
 * that **the first quarter of a model's bpw range is more interesting than the
 * last quarter**, whatever the range happens to be. Nearly every model lives in
 * its bottom quarter — 3.2 against 4.5 is a real difference someone wants to
 * see, 20 against 24 is not — and the top is usually a handful of unquantized
 * norms that only need to read as "much heavier" (owner, 2026-08-10).
 *
 * The cube root delivers exactly that, and **the emphasis is scale-free**: the
 * curve acts on the normalised fraction, which depends only on `v/min` against
 * `max/min`, so it has already forgotten the absolute numbers. The first 25% of
 * any range gets 63.0% of the ramp and the last 25% gets 9.1% — a constant
 * 6.9x, identical for 4→32, 2→8, 3.2→6.5 and 100→10000. That invariance is the
 * requirement; the exact ratio is not, which is why a plainer exponent beats a
 * tuned one.
 *
 * A `p` solved for "first octave exactly twice the last" (0.6728) was tried and
 * dropped: it is defined against an octave *count*, so the ratio drifts as the
 * domain widens — the opposite of scale-free — and the weaker curve left 4.5
 * and 5.2 two points of saturation apart.
 */
const LOW_END_EMPHASIS = 1 / 3;

/** The observed range of a metric across the grid, ignoring empty cells. */
function metricRange(
  anatomy: Anatomy,
  metric: AnatomyMetric,
): { readonly min: number; readonly max: number } {
  let min = Number.POSITIVE_INFINITY;
  let max = 0;
  for (const cell of anatomy.cells.values()) {
    const value = metricValue(cell, metric);
    if (value <= 0) continue;
    min = Math.min(min, value);
    max = Math.max(max, value);
  }
  return { min: Number.isFinite(min) ? min : 0, max };
}

/**
 * Distinct dtypes get categorical slots, in first-seen order.
 *
 * Assigned by identity and never re-assigned by rank, so a dtype keeps its
 * colour as the view changes. Past eight distinct dtypes — which no real repo
 * has — everything folds into the last slot rather than generating a hue.
 */
function assignDtypeSlots(anatomy: Anatomy): Map<string, number> {
  const slots = new Map<string, number>();
  distinctDtypes(anatomy).forEach((dtype, index) => slots.set(dtype, index));
  return slots;
}

function assignDtypeColors(anatomy: Anatomy, palette: MapPalette): Map<string, string> {
  const colors = new Map<string, string>();
  for (const [dtype, index] of assignDtypeSlots(anatomy)) {
    colors.set(dtype, slot(palette.series, index));
  }
  return colors;
}

/**
 * Read a palette slot, folding anything past the last one into it.
 *
 * `noUncheckedIndexedAccess` makes the out-of-range case explicit, which is the
 * right outcome anyway: a ninth dtype should reuse slot eight rather than make
 * the renderer emit `undefined` as a paint value.
 */
function slot(list: readonly string[], index: number): string {
  return list[Math.min(index, list.length - 1)] ?? list[0] ?? "none";
}

/**
 * The ramp's two ends, for a legend that has to state what the shading spans.
 *
 * A function rather than direct indexing so the palette's length stays the
 * renderer's business: a caller reaching for `ramp[8]` would be asserting the
 * step count from outside.
 */
/**
 * Is the grid shading the difference rather than A's own value?
 *
 * Exported so `ui/anatomy-map.ts` can word the compare note from the same
 * answer the legend is built from. Two independent decisions about what the
 * colour encodes is how a page comes to explain one picture and draw another.
 */
export function comparesByDifference(
  anatomy: Anatomy,
  compare: Anatomy | undefined,
  metric: AnatomyMetric,
): boolean {
  return compare !== undefined && diffSpan(anatomy, compare, metric) !== undefined;
}

/** The diverging scale's poles, for a legend that must state its direction. */
function divergeEndpoints(palette: MapPalette): {
  readonly low: string;
  readonly high: string;
} {
  return {
    low: slot(palette.diverge, 0),
    high: slot(palette.diverge, palette.diverge.length - 1),
  };
}

function rampEndpoints(palette: MapPalette): {
  readonly low: string;
  readonly high: string;
} {
  return {
    low: slot(palette.ramp, 0),
    high: slot(palette.ramp, palette.ramp.length - 1),
  };
}

/* -- legend ---------------------------------------------------------------- */

/**
 * A metric value with its unit spelled out.
 *
 * `formatBytes` already carries its unit, so naming the metric after it would
 * read "1.2 MiB bytes"; everything else needs the name to mean anything.
 */
function metricPhrase(
  value: number,
  metric: AnatomyMetric,
  mode: Anatomy["mode"],
): string {
  if (metric === "bytes") return formatMetric(value, metric);
  return `${formatMetric(value, metric)} ${metricLabel(metric, mode)}`;
}

/** `ramp` and `categorical` carry swatches; `uniform` is a sentence, no colour. */
export type LegendKind = "ramp" | "categorical" | "uniform";

export interface LegendKey {
  readonly color: string;
  readonly label: string;
}

export interface LegendModel {
  readonly kind: LegendKind;
  /** Empty for `uniform`, by construction. */
  readonly keys: readonly LegendKey[];
  /** Names the encoding for `ramp`/`categorical`; states the value for `uniform`. */
  readonly note: string;
}

/**
 * What the legend has to say, decided once for both consumers.
 *
 * The browser draws it as HTML and the card draws it as SVG, so the *decision*
 * lives here and only the marks differ. Two copies of this branch drifted the
 * moment one of them learned about a degenerate domain.
 *
 * **A one-value scale is not a scale.** When every cell holds the same value the
 * ramp encodes nothing, and printing its endpoints actively misleads: on a
 * uniformly-F32 repo the legend read `■ 32.00  ■ 32.00`, painting the ramp's
 * lightest and darkest steps beside two identical numbers while every cell in
 * the grid was painted the ramp's *middle* step — three colours on screen, none
 * of which appeared in the chart they claimed to explain. So a degenerate domain
 * collapses to one sentence and drops the ramp entirely. Same rule for a
 * single-dtype categorical map: one swatch is not an identity scheme, it is a
 * fact, and the data-viz rule that a single series needs no legend box applies.
 */
export function legendModel(
  anatomy: Anatomy,
  metric: AnatomyMetric,
  palette: MapPalette,
  compare?: Anatomy,
): LegendModel {
  /*
   * Compare mode's diverging scale gets its own legend, and must: the swatches
   * are the *only* thing on screen that says the colour changed meaning. A
   * reader who opened the second slot and saw the grid recolour needs the key
   * beside it to read "how far this cell is from B's", not A's range in a hue
   * it no longer uses.
   *
   * Before `dtype`, because a categorical metric has no difference to shade and
   * falls through to the branch below unchanged.
   */
  const span = compare ? diffSpan(anatomy, compare, metric) : undefined;
  if (span === 0) {
    /*
     * Agreement everywhere. One swatch and a sentence, not a three-key scale
     * whose ends both read `0.00` — the same rule the sequential ramp follows
     * for a degenerate domain, and for the same reason: a key with nothing to
     * separate encodes nothing.
     */
    return {
      kind: "uniform",
      keys: [],
      note: `${metricLabel(metric, anatomy.mode)} · identical on every shared cell`,
    };
  }
  if (span !== undefined) {
    const { low, high } = divergeEndpoints(palette);
    return {
      kind: "ramp",
      keys: [
        // Signed, and explicitly so. `−1.50` alone would read as a value on a
        // scale rather than as a distance from agreement in a direction.
        { color: low, label: `A −${formatMetric(span, metric)}` },
        { color: slot(palette.diverge, 4), label: "same" },
        { color: high, label: `A +${formatMetric(span, metric)}` },
      ],
      note: `${metricLabel(metric, anatomy.mode)} · shading is A minus B`,
    };
  }

  if (metric === "dtype") {
    const colors = assignDtypeColors(anatomy, palette);
    const only = [...colors.keys()][0];
    if (colors.size <= 1) {
      return {
        kind: "uniform",
        keys: [],
        note: only ? `every tensor is stored as ${only}` : "nothing to map",
      };
    }
    return {
      kind: "categorical",
      keys: [...colors].map(([dtype, color]) => ({ color, label: dtype })),
      note: metricLabel(metric, anatomy.mode),
    };
  }

  const { min, max } = metricRange(anatomy, metric);
  // Same test `rampStep` uses to decide it cannot place a value on the ramp, so
  // the legend and the shading agree about what "degenerate" means.
  if (!(max > min)) {
    return {
      kind: "uniform",
      keys: [],
      note: `uniform across the map: ${metricPhrase(max, metric, anatomy.mode)}`,
    };
  }
  const { low, high } = rampEndpoints(palette);
  return {
    kind: "ramp",
    keys: [
      { color: low, label: formatMetric(min, metric) },
      { color: high, label: formatMetric(max, metric) },
    ],
    note:
      metricLabel(metric, anatomy.mode) + (isLogScale(metric) ? " · log scale" : ""),
  };
}

/* -- shared cell colouring ------------------------------------------------- */

interface Shading {
  readonly metric: AnatomyMetric;
  readonly min: number;
  readonly max: number;
  readonly palette: MapPalette;
  readonly dtypeSlots: Map<string, number> | undefined;
  /**
   * Compare mode's diverging scale: the largest |A − B| on the grid.
   *
   * Symmetric about zero on purpose, and `undefined` when the fill still
   * encodes A — see `diffSpan`.
   */
  readonly diffSpan: number | undefined;
}

/** A cell's fill, and the ink that stays legible on top of it. */
interface CellPaint {
  readonly fill: string;
  readonly ink: string;
}

function shadingFor(
  anatomy: Anatomy,
  metric: AnatomyMetric,
  palette: MapPalette,
  compare?: Anatomy,
): Shading {
  const { min, max } = metricRange(anatomy, metric);
  return {
    metric,
    min,
    max,
    palette,
    dtypeSlots: metric === "dtype" ? assignDtypeSlots(anatomy) : undefined,
    diffSpan: compare ? diffSpan(anatomy, compare, metric) : undefined,
  };
}

/**
 * The half-width of the diverging scale: the largest |A − B| anywhere on the
 * grid, or `undefined` when the difference cannot be shaded at all.
 *
 * **Symmetric about zero**, rather than fitted to the observed min and max. An
 * asymmetric diverging scale moves the neutral colour off zero, so a cell where
 * the two models agree would be painted as a small difference — which is the
 * one thing this scale exists to make visible.
 *
 * **Zero is a span, not an absence** (owner, 2026-08-12). Two models that agree
 * on every cell return `0` and the grid paints entirely neutral. This first
 * fell back to the sequential ramp on the reasoning that a one-colour scale
 * implies a gradient nobody can read — but the fallback silently changes what
 * the colour *means*, which is the objection this file raises against A-only
 * shading two screens up. A uniformly neutral grid under a legend that says
 * "identical on every cell" is the honest picture of no difference; A's
 * magnitude ramp under a legend about differences is not.
 *
 * `undefined` in exactly two cases, both of which mean there is no difference
 * to speak of rather than a difference of zero:
 *
 *  - `dtype`, which is categorical — "BF16 minus U8" is not a quantity, so the
 *    metric keeps A's own colouring and the legend says which is in force;
 *  - **no shared cell at all**, where every square would be bare plane and
 *    "identical everywhere" would be a claim about an empty set.
 */
function diffSpan(
  anatomy: Anatomy,
  compare: Anatomy,
  metric: AnatomyMetric,
): number | undefined {
  if (metric === "dtype") return undefined;
  let span = 0;
  let shared = false;
  for (const [key, cell] of anatomy.cells) {
    const other = compare.cells.get(key);
    // A cell missing from B has no difference. Not zero — absent; see
    // `paintFor`, which draws it as plane rather than as agreement.
    if (!other) continue;
    shared = true;
    span = Math.max(
      span,
      Math.abs(metricValue(cell, metric) - metricValue(other, metric)),
    );
  }
  return shared ? span : undefined;
}

/**
 * The fill and its ink, resolved together from one slot index.
 *
 * Together and not separately, because they are two halves of one decision: an
 * ink chosen from a different index than the fill it sits on is exactly the
 * unreadable cell the parallel token arrays exist to prevent. See
 * `MapPalette.rampInk`.
 */
function paintFor(
  cell: AnatomyCell,
  shading: Shading,
  bCell?: AnatomyCell | undefined,
): CellPaint {
  const { palette } = shading;
  if (shading.dtypeSlots) {
    const index =
      shading.dtypeSlots.get(cell.dtypes[0] ?? "") ?? palette.series.length - 1;
    return { fill: slot(palette.series, index), ink: slot(palette.seriesInk, index) };
  }

  /*
   * Compare mode shades the difference. The module header carries the full
   * argument, including the two objections this honours rather than overrules.
   */
  if (shading.diffSpan !== undefined) {
    if (!bCell) {
      /*
       * In A and not in B. The ragged edge is a *finding*, so it is drawn as
       * one: the plane, which is the same "nothing here" this grid already uses
       * for a missing cell. Painting it neutral would say the two models agree.
       */
      return { fill: palette.surface, ink: palette.inkMuted };
    }
    /*
     * Neither side has this quantity, so there is no difference to shade.
     *
     * Subtracting would give `0 - 0`, which lands on the neutral step and says
     * **the two models agree on their bits per weight here** — in a cell whose
     * own label is an em dash saying there is no such number. That is the
     * defect one metric over: `94e8b5db` was a blank where a value was
     * expected, and this is agreement asserted about a quantity neither model
     * has. The plane is already this grid's word for "nothing here", used four
     * lines up for a cell missing from B, so a buffer row now reads the same
     * off-scale in compare as it does in the single-model view.
     *
     * Either side is enough. In practice both are buffers — the row's identity
     * decides that, not the cell's — but a difference is undefined the moment
     * one operand is, and testing both says so without relying on the layout.
     */
    if (
      cellValueKind(cell, shading.metric, metricValue(cell, shading.metric)) ===
        "not-applicable" ||
      cellValueKind(bCell, shading.metric, metricValue(bCell, shading.metric)) ===
        "not-applicable"
    ) {
      return { fill: palette.surface, ink: palette.inkMuted };
    }
    const delta =
      metricValue(cell, shading.metric) - metricValue(bCell, shading.metric);
    /*
     * Zero maps to 0.5 exactly, which is the neutral step — including when the
     * span itself is zero, where every shared cell agrees and dividing by it
     * would be `NaN`. Guarded here rather than by refusing the scale, because
     * "they are the same everywhere" is a result worth painting.
     */
    const fraction =
      shading.diffSpan === 0 ? 0.5 : unitFraction(0.5 + delta / (2 * shading.diffSpan));
    return { fill: palette.divergeAt(fraction), ink: palette.divergeInkAt(fraction) };
  }

  const value = metricValue(cell, shading.metric);
  /*
   * A cell with no position on this scale is drawn off it.
   *
   * A buffer's bits-per-weight does not exist — bits *per weight* needs weights
   * — so `metricValue` returns 0 and the ramp would put it at the pale end,
   * where it reads as *a very low bits-per-weight* rather than as *no such
   * quantity*. `cellLabel` already prints an em dash there; painting the cell
   * on the value scale contradicts it, which is a quieter version of the defect
   * report 94e8b5db was about.
   *
   * The plane is the vocabulary this function already uses for "nothing here",
   * a few lines up, for a cell present in A and absent from B. Reusing it keeps
   * one visual meaning rather than inventing a second neutral.
   *
   * Deliberately per metric, not per cell. In `params` a buffer's zero is a
   * **real** position on a real scale, so it keeps its ramp colour and its `0`;
   * in `bytes` it has an ordinary size. Only `bpw` is inapplicable, and only
   * `bpw` goes off-scale (reviewer, 2026-08-18).
   */
  if (cellValueKind(cell, shading.metric, value) === "not-applicable") {
    return { fill: palette.surface, ink: palette.inkMuted };
  }

  const fraction = rampFraction(value, shading.min, shading.max, shading.metric);
  return { fill: palette.rampAt(fraction), ink: palette.rampInkAt(fraction) };
}

/** Clamp to [0, 1]; a delta exactly at the span lands on a pole. */
function unitFraction(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/* -- public entry point ---------------------------------------------------- */

export interface MapBandSize {
  readonly width: number;
  readonly height: number;
}

export interface InteractiveMapLayout {
  /** The pinned row-label gutter, and the caption naming the scrollable axis. */
  readonly labels: MapBandSize;
  /** The scrollable band: one column per layer, and the tick strip. */
  readonly layers: MapBandSize;
  /** The pinned non-layer column, or `undefined` when the model has none. */
  readonly global: MapBandSize | undefined;
  /** All three side by side — what showing the whole map at once would take. */
  readonly total: MapBandSize;
}

/** Columns are the same pitch in every band, so a cell is a square everywhere. */
const PITCH = CELL + GAP;

/**
 * Width of the pinned heading for the non-layer column.
 *
 * Wide enough for the cell plus the word above it, from the advance
 * approximation and rounded up — the same rule the card's gutter uses, and for
 * the same reason: there is nothing here to measure text with.
 */
const GLOBAL_BAND_WIDTH = Math.max(
  CELL + GAP * 2,
  Math.ceil(GLOBAL_LABEL.length * AXIS_FONT * MONO_ADVANCE_EM) + 12,
);

/** The gutter floor, so the axis caption is never wider than the band holding it. */
const AXIS_CAPTION_WIDTH =
  Math.ceil(AXIS_CAPTION.length * AXIS_FONT * MONO_ADVANCE_EM) + 14;

/**
 * The row-label gutter, sized to the names it has to hold.
 *
 * Was a flat 96px. Deriving it costs nothing and buys back ~16px of scrollable
 * width on a typical transformer — which matters at 360px, where 96px of gutter
 * was a quarter of the viewport. Long names are truncated rather than allowed to
 * widen the gutter without bound; 14 characters covers every role the rule table
 * produces.
 */
const ROW_LABEL_MAX_CHARS = 14;

function rowLabelWidth(anatomy: Anatomy): number {
  const longest = anatomy.roles.reduce((n, role) => Math.max(n, role.length), 0);
  const forRoles =
    Math.ceil(
      Math.min(longest, ROW_LABEL_MAX_CHARS) * ROW_LABEL_FONT * MONO_ADVANCE_EM,
    ) + 14;
  return Math.max(forRoles, AXIS_CAPTION_WIDTH);
}

function layerColumnCount(anatomy: Anatomy): number {
  let count = 0;
  for (const layer of anatomy.layers) if (layer !== undefined) count += 1;
  return count;
}

function hasGlobalColumn(anatomy: Anatomy): boolean {
  return anatomy.layers.some((layer) => layer === undefined);
}

/**
 * The natural size of each band of the interactive map.
 *
 * Exported because the interactive consumer sizes three boxes from it and
 * `MapRenderOptions` takes a size rather than computing one — the card consumer
 * needs to *impose* 1200×630, and a renderer that sometimes computes its own
 * size and sometimes does not is the kind of ambiguity that produces two
 * layouts again.
 *
 * Every band is the same height, which is what makes the pinned columns line up
 * with the scrolled ones without a second copy of the row arithmetic.
 */
/**
 * The band the per-layer marginals occupy, above the axis ticks.
 *
 * Added to the height only when marginals are drawn (`withMarginals`), so a map
 * without them — every card, and the page before this feature — keeps its exact
 * previous geometry rather than growing a strip of empty space.
 */
const MARGINAL_BAND = 18;

/**
 * Trailing space inside the label band, in place of the container's padding.
 *
 * Mirrors `--space-2` (0.5rem = 8px), which is what `.anatomy-pinned--labels`
 * used to apply as `padding-right`. A literal here because SVG coordinates are
 * pixels and this renderer may not read CSS — the same reason `CELL` and `GAP`
 * are numbers. If the token moves, this moves with it.
 */
const LABEL_TRAIL = 8;

/** Font for both marginals. Smaller than the role label it sits under. */
const MARGINAL_FONT = 10;

export function interactiveMapLayout(
  anatomy: Anatomy,
  withMarginals = false,
): InteractiveMapLayout {
  const height =
    anatomy.roles.length * PITCH + AXIS_HEIGHT + (withMarginals ? MARGINAL_BAND : 0);
  /*
   * The label band carries its own trailing space instead of taking it from the
   * container's `padding-right` (owner, 2026-08-14).
   *
   * With the padding outside the SVG, anything drawn to the SVG's right edge —
   * the marginal rule — stopped 8px short of the vertical border beside it, so
   * the two rules that are meant to frame the grid did not meet. Moving the gap
   * inside means the SVG's edge *is* the border, and the rule reaches it.
   *
   * Visually identical: the role names are still positioned from
   * `rowLabelWidth`, so they sit exactly where they did; only the box around
   * them grew by the space that used to be padding. `.anatomy-pinned--labels`
   * drops its `padding-right` to match — the two must change together.
   */
  const labels = { width: rowLabelWidth(anatomy) + LABEL_TRAIL, height };
  const layers = { width: layerColumnCount(anatomy) * PITCH, height };
  const global = hasGlobalColumn(anatomy)
    ? { width: GLOBAL_BAND_WIDTH, height }
    : undefined;
  return {
    labels,
    layers,
    global,
    total: {
      width: labels.width + layers.width + (global?.width ?? 0),
      height,
    },
  };
}

export function renderMapSvg(options: MapRenderOptions): string {
  return options.mode === "card"
    ? renderCardSvg(options)
    : renderInteractiveSvg(options);
}

/* -- interactive ----------------------------------------------------------- */

/**
 * One cell's value, spoken in full.
 *
 * The `dtype` branch is not decoration. Without it this fell through to
 * `formatMetric`, which for `dtype` formats `metricValue`'s fallback — the
 * effective bits per weight — so the dtype map announced every cell as "4.50
 * storage dtype": a number that is not the quantity named beside it, in the one
 * rendering a screen-reader user has. The in-cell label always printed the
 * dtype; only the accessible name disagreed, which is why it survived.
 */
function cellValuePhrase(
  cell: AnatomyCell,
  metric: AnatomyMetric,
  anatomy: Anatomy,
): string {
  if (metric === "dtype") {
    // `dtypes` is bytes-ordered, so the first name is what the cell is mostly
    // made of and the note below is about that one.
    return `stored as ${cell.dtypes.join(", ")}${indexTableNote(cell.dtypes[0])}`;
  }
  return `${formatMetric(metricValue(cell, metric), metric)} ${metricLabel(metric, anatomy.mode)}`;
}

/**
 * 64-bit integer dtypes, which in a checkpoint are never weights.
 *
 * `deepseek-ai/DeepSeek-V4-Flash-0731` routes experts by hash on its first
 * three layers, so its router cells there are `I64 [129280, 6]` lookup tables.
 * That is the model being unusual and the map being right, but it reads as a
 * glitch: a reader who has learned that this view shows how weights are stored
 * sees a dtype no weight is ever stored in (reported via Reddit, 2026-08-08).
 *
 * **Only 64-bit.** The obvious rule — "integers are not weights" — is false, and
 * exactly backwards on the model that prompted this: DeepSeek's expert weights
 * *are* `I8`, and MLX packs 4-bit weights into `U32`. Anything 32 bits or
 * narrower is a plausible container for packed weights, so claiming otherwise
 * would trade a confusing cell for a wrong one. At 64 bits the argument is
 * airtight in a way it is not at 32: eight bytes per weight is larger than the
 * float it would replace, so no quantizer would ever choose it, which leaves
 * indices and lookup tables as the only use.
 */
const INDEX_DTYPES: ReadonlySet<string> = new Set(["I64", "U64"]);

function indexTableNote(dominant: string | undefined): string {
  return dominant && INDEX_DTYPES.has(dominant)
    ? " — integer index tables, not weights"
    : "";
}

function cellWhere(cell: AnatomyCell): string {
  return cell.layer === undefined ? cell.role : `layer ${cell.layer} ${cell.role}`;
}

function cellAriaLabel(
  cell: AnatomyCell,
  metric: AnatomyMetric,
  anatomy: Anatomy,
): string {
  return `${cellWhere(cell)}: ${cellValuePhrase(cell, metric, anatomy)}`;
}

/**
 * The compare cell's accessible name — the one place the two em-dash cases are
 * told apart.
 *
 * "B has no such tensors" and "B could not resolve a value" print the same
 * single character in a 30px cell (see `NO_VALUE`), so if they are not
 * distinguished here they are not distinguished anywhere a keyboard user goes.
 */
function compareCellAriaLabel(
  cell: AnatomyCell,
  bCell: AnatomyCell | undefined,
  metric: AnatomyMetric,
  anatomy: Anatomy,
  compare: Anatomy,
): string {
  const b = bCell
    ? `B ${cellValuePhrase(bCell, metric, compare)}`
    : "B has no tensors here";
  return `${cellWhere(cell)}: A ${cellValuePhrase(cell, metric, anatomy)}, ${b}`;
}

/** One drawn column, already placed in its band's coordinate space. */
interface BandColumn {
  readonly layer: number | undefined;
  readonly x: number;
}

/**
 * Which columns a band draws, and where.
 *
 * The split is the whole point: `layers` gets the unbounded axis and nothing
 * else, so it is the only band that ever needs to scroll.
 */
function bandColumns(anatomy: Anatomy, band: MapBand): readonly BandColumn[] {
  if (band === "labels") return [];
  if (band === "global") {
    return hasGlobalColumn(anatomy)
      ? [{ layer: undefined, x: (GLOBAL_BAND_WIDTH - CELL) / 2 }]
      : [];
  }
  const columns: BandColumn[] = [];
  for (const layer of anatomy.layers) {
    if (layer === undefined) continue;
    columns.push({ layer, x: EDGE + columns.length * PITCH });
  }
  return columns;
}

/**
 * The strip under the grid: layer ticks, the axis caption, the pinned column's
 * heading.
 *
 * Set in the mono stack so the advance approximation that reserved the band's
 * width is the same one the glyphs actually use.
 */
function renderBandAxis(
  band: MapBand,
  columns: readonly BandColumn[],
  width: number,
  height: number,
  palette: MapPalette,
): string {
  const y = height - 16;
  const tick = (x: number, label: string, anchor: "middle" | "end"): string =>
    `<text x="${num(x)}" y="${num(y)}" text-anchor="${anchor}" font-size="${AXIS_FONT}"
      fill="${palette.faint}" font-family="${escapeXml(palette.fontMono)}">${escapeXml(label)}</text>`;

  /*
   * The caption sits in the pinned gutter, on the ticks' own baseline, so it
   * reads as the axis title it is — and so it does not scroll away from the
   * ticks it names.
   *
   * **Body ink and bold, not the tick's `faint`** (owner, 2026-08-11). Sharing
   * the ticks' weight and colour put it in the same visual class as the *role
   * labels* directly above it, in the same column and the same mono face — so
   * `layer →` read as one more row name, the last entry in a list of components.
   * It is the opposite: it names the other axis. Weight and ink are what say
   * "heading" here, since position cannot — the gutter is shared.
   *
   * Card ticks are unaffected: they carry the caption's job in their own `L`
   * prefix and never reach this branch.
   */
  if (band === "labels") {
    return `<text x="${num(width - 10)}" y="${num(y)}" text-anchor="end" font-size="${AXIS_FONT}"
      fill="${palette.ink}" font-weight="700" font-family="${escapeXml(palette.fontMono)}">${escapeXml(AXIS_CAPTION)}</text>`;
  }

  return columns
    .map(({ layer, x }) => {
      // The non-layer column gets a word, not a dot: `·` reads as a rendering
      // artifact, which is the last thing a column holding a quarter of the
      // model's bytes should look like.
      if (layer === undefined) return tick(x + CELL / 2, GLOBAL_LABEL, "middle");
      // Ticks every five, matching the reference. Dense enough to locate a
      // column, sparse enough not to become a wall of numbers at 100+ layers.
      return layer % 5 === 0 ? tick(x + CELL / 2, String(layer), "middle") : "";
    })
    .join("");
}

/**
 * A cell's annotation: one centred value, or A over B on two baselines.
 *
 * Both labels take **the same ink**, because both sit on the same fill — the
 * fill encodes A alone (see `COMPARE_LABEL_FONT`). That is the whole contrast
 * argument, and it is an argument from reuse rather than a new claim: `paintFor`
 * resolves fill and ink together from one ramp index precisely so an ink can
 * never be chosen against a colour it is not printed on, and `cell-labels.test.ts`
 * already proves every step of that pairing clears 4.5:1 in all three theme
 * blocks. Dimming B to tell it from A would have broken exactly that guarantee;
 * B is told from A by its baseline and by the legend, never by its colour —
 * which is also the data-viz rule that text wears text tokens, not series ones.
 */
function cellAnnotation(
  at: { readonly x: number; readonly y: number },
  ink: string,
  fontMono: string,
  parts: ReadonlyArray<{
    readonly text: string;
    readonly dx: number;
    readonly dy: number;
    readonly size: number;
    readonly anchor: "start" | "middle" | "end";
    /**
     * `a`/`b` in compare mode; absent for the solo label, whose class stays
     * exactly what it has always been. A modifier nothing styles and nothing
     * queries would only be a second name for the same element.
     */
    readonly variant?: string;
  }>,
): string {
  return parts
    .map(
      (part) =>
        // `pointer-events="none"` is load-bearing, not tidiness. The hover
        // detail, the hover ring and the click-through are all bound to the
        // `<rect>`; a `<text>` drawn over its middle would swallow the pointer
        // and the cell would stop responding in its centre — a defect that
        // leaves the markup, the listeners and the tests all correct.
        //
        // `aria-hidden` for the same class of reason from the other side: the
        // rect's own `aria-label` already reads the values out in full, and a
        // second, abbreviated copy would be announced twice.
        // `enhanceAnatomyMap` hides every `<text>` in the grid anyway; saying
        // it here means the markup is right before anything enhances it.
        `<text class="anatomy-cell-value${
          part.variant ? ` anatomy-cell-value--${part.variant}` : ""
        }" x="${num(
          at.x + part.dx,
        )}" y="${num(at.y + part.dy)}" text-anchor="${part.anchor}" font-size="${num(
          part.size,
        )}" font-family="${escapeXml(fontMono)}" fill="${ink}"
            pointer-events="none" aria-hidden="true">${escapeXml(part.text)}</text>`,
    )
    .join("");
}

/**
 * A marginal, printed in the units the active measure uses (SPEC §5).
 *
 * The label must name the measure it shows, and here the *format* does that job
 * — `5.50` is a width, `2.10 GiB` is a size, `58.7M` is a count, and no reader
 * confuses them. That is why there is no "avg" prefix: it would be a lie on two
 * of the three, since bytes and params are sums (SPEC §2.2).
 *
 * `dtype` mode gets nothing. No scheme was recognized, so there is no bits per
 * weight to average and fabricating one would be the wrong-but-authoritative
 * number this whole feature is careful to avoid.
 *
 * A trailing `*` marks a group where some cell could not be resolved and so
 * contributed nothing (SPEC §2.3). Silence there would present an average over
 * a subset as an average over the whole — the same defect as the IQ message
 * that hid a coverage gap.
 *
 * ## Why the width differs by axis
 *
 * SPEC §5 asks for a unit policy applied **per axis, not per cell**, and the two
 * axes have very different budgets. The family marginal and the corner sit in
 * the label gutter, which is wide, so they take the long form and read in the
 * same units as the header — `10.8 MiB`, `1.95 GiB`.
 *
 * The per-layer strip does not: a column is about 34px, and `7.6 MiB` beside
 * `21.6 MiB` beside `27.3 MiB` overlapped into an unreadable smear on the bytes
 * measure (owner, 2026-08-14). The fix is not a line break or dropping the unit
 * — it is `compactMagnitude`, which exists for exactly this and is what the
 * cells directly above already use. Its own comment says the long forms "do not
 * fit in a 30px cell and would render as a smear rather than as a number",
 * which is precisely what happened.
 *
 * That also makes the strip *more* useful, not just narrower: a column total of
 * `7.6M` under cells reading `336K` is directly comparable to them, where a
 * differently-formatted number would not be.
 */
function marginalText(
  marginal: Marginal,
  metric: AnatomyMetric,
  width: "long" | "compact",
): string {
  // Buffers are subtracted because they are not unresolved — see `buffers` on
  // `Marginal`. Without this every gpt2 row carried a coverage warning about a
  // checkpoint in which nothing is unknown.
  const excluded = marginal.total - marginal.resolved - marginal.buffers;
  const mark = excluded > 0 ? "*" : "";
  if (metric === "dtype") return "";
  if (metric === "bytes") {
    return (
      (width === "long"
        ? formatBytes(marginal.bytes)
        : compactMagnitude(marginal.bytes, ["B", "K", "M", "G", "T"], 1024)) + mark
    );
  }
  if (metric === "params") {
    return (
      (width === "long"
        ? formatParams(marginal.params)
        : compactMagnitude(marginal.params, ["", "K", "M", "B"], 1000)) + mark
    );
  }
  /*
   * bpw is two decimals either way — four characters already, and the one
   * measure whose scale never changes.
   *
   * `undefined` here means the group contributed no parameters, and the reason
   * decides what to print — the same distinction `cellValueKind` draws for a
   * cell. A group that is **all buffers** has no bits-per-weight because it has
   * no weights, which is an em dash; a group that is unresolved has one we could
   * not work out, which stays blank.
   *
   * Before this, a buffer row's margin was blank while its `params` margin read
   * `0` — the two formatters answering "what does zero mean" differently for one
   * row, which is what `cellValueKind` exists to stop.
   */
  if (marginal.bpw !== undefined) return marginal.bpw.toFixed(2) + mark;
  const allBuffers = marginal.total > 0 && marginal.buffers === marginal.total;
  return allBuffers ? NOT_APPLICABLE : "";
}

/**
 * The per-layer marginals, and the corner where the two axes meet.
 *
 * One strip immediately above the axis ticks, aligned to the columns, in every
 * band — so a scrolled column carries its own total and the pinned label band
 * carries the grand one. That split is what keeps the corner visible while the
 * columns move, and it costs nothing: the columns' x positions are the same
 * ones the cells above used.
 *
 * The corner is the self-check SPEC §5 asks for — the rows and the columns are
 * two projections of one grid, and printing the whole beside them is what lets
 * a reader confirm the parts add up. It is deliberately the *computed* total
 * rather than a value copied from the header, so the two agreeing is evidence
 * rather than tautology.
 */
function renderMarginalStrip(
  options: MapRenderOptions,
  band: MapBand,
  columns: readonly BandColumn[],
  width: number,
  height: number,
  palette: MapPalette,
): string {
  const marginals = options.marginals;
  if (!marginals) return "";
  /*
   * Baseline inside the band this feature added, not inside the axis strip.
   *
   * `height - AXIS_HEIGHT` is the *bottom* of the last cell row plus the
   * marginal band, which put these numbers eight pixels above the layer ticks —
   * two rows of small mono digits close enough to read as one (caught on the dev
   * server, not by a test; a collision is a fact about glyphs, and the fixtures
   * only know about strings). Subtracting the band lands them directly under the
   * grid, leaving the ticks their own line.
   */
  const bandTop = height - AXIS_HEIGHT - MARGINAL_BAND;
  const y = bandTop + MARGINAL_FONT + 4;

  /*
   * A hairline between the grid and the axis totals, matching the vertical rule
   * `.anatomy-pinned--labels` already draws between the role names and the
   * cells (owner, 2026-08-14). Same `--border` token and the same 1px, so the
   * two read as one convention rather than two separate decisions.
   *
   * It earns its keep because there is now a number under every role name *and*
   * a number under every column: without a break, the bottom row of cells and
   * the marginal strip read as one continuous block of digits.
   *
   * Drawn in every band across its full width, so the three separately-scrolled
   * SVGs join into one unbroken line. Offset half a pixel because a 1px stroke
   * centred on an integer coordinate straddles two device pixels and renders as
   * a soft grey smear instead of a line.
   */
  const rule = `<line x1="0" y1="${num(bandTop + 0.5)}" x2="${num(width)}" y2="${num(bandTop + 0.5)}"
      stroke="${palette.border}" stroke-width="1"/>`;

  if (band === "labels") {
    const total = marginalText(marginals.total, options.metric, "long");
    if (total === "") return rule;
    // Bold and body ink, for `layer →`'s reason one line below: in the same
    // gutter and the same face, a muted number would read as one more family
    // marginal rather than as the total of all of them.
    return `${rule}<text x="${num(width - 10)}" y="${num(y)}" text-anchor="end" font-size="${MARGINAL_FONT}"
      fill="${palette.ink}" font-weight="700" font-family="${escapeXml(palette.fontMono)}">${escapeXml(total)}</text>`;
  }

  return (
    rule +
    columns
      .map(({ layer, x }) => {
        const marginal = marginals.byLayer.get(layer);
        if (!marginal) return "";
        const label = marginalText(marginal, options.metric, "compact");
        if (label === "") return "";
        return `<text x="${num(x + CELL / 2)}" y="${num(y)}" text-anchor="middle" font-size="${MARGINAL_FONT}"
        fill="${palette.faint}" font-family="${escapeXml(palette.fontMono)}">${escapeXml(label)}</text>`;
      })
      .join("")
  );
}

function renderInteractiveSvg(options: MapRenderOptions): string {
  const { anatomy, metric, width, height, palette } = options;
  const band = options.band ?? "layers";
  const compare = options.compare;
  // The interactive path only. `renderCardSvg` below depicts one model, so a
  // share card keeps the sequential ramp and cannot acquire a second colour
  // system by accident.
  const shading = shadingFor(anatomy, metric, palette, compare);
  const columns = bandColumns(anatomy, band);
  const labelWidth = rowLabelWidth(anatomy);
  // One decision for the whole grid, taken before any cell is drawn. See
  // `labelsFit`.
  const annotate = labelsFit(anatomy, metric, compare);

  const rows = anatomy.roles
    .map((role, rowIndex) => {
      const y = EDGE + rowIndex * PITCH;
      /*
       * The family marginal is a **second line under the role name**, inside the
       * row's existing height (SPEC §5) — the name keeps its baseline shifted up
       * and the number sits below it, smaller and muted. Nothing is added to
       * `PITCH`, so the grid does not shift when the feature turns on and the
       * pinned label band still lines up with the scrolled columns.
       */
      const roleMarginal = options.marginals?.byRole.get(role);
      const nameY = roleMarginal ? y + CELL / 2 - 2 : y + CELL / 2 + 4;
      const label =
        band === "labels"
          ? `<text x="${num(labelWidth - 10)}" y="${num(nameY)}" text-anchor="end"
        font-size="${ROW_LABEL_FONT}" fill="${palette.inkMuted}" font-family="${escapeXml(palette.fontMono)}">${escapeXml(
          fitMono(role, ROW_LABEL_FONT, labelWidth - 12),
        )}</text>${
          roleMarginal
            ? `<text x="${num(labelWidth - 10)}" y="${num(y + CELL / 2 + 10)}" text-anchor="end"
        font-size="${MARGINAL_FONT}" fill="${palette.faint}" font-family="${escapeXml(palette.fontMono)}">${escapeXml(
          marginalText(roleMarginal, metric, "long"),
        )}</text>`
            : ""
        }`
          : "";

      const cells = columns
        .map(({ layer, x }) => {
          const key = cellKey(layer, role);
          const cell = anatomy.cells.get(key);
          // Absent, not empty — a gap means "this role does not exist at this
          // layer", which is a different statement from "its value is zero".
          // True of A's grid in compare mode too: the drawn shape stays A's.
          if (!cell) return "";
          const bCell = compare?.cells.get(key);
          const paint = paintFor(cell, shading, bCell);
          const rect = `<rect class="anatomy-cell" x="${num(x)}" y="${num(y)}" width="${CELL}" height="${CELL}"
            rx="5" fill="${paint.fill}" tabindex="0" role="gridcell"
            data-layer="${layer ?? ""}" data-role="${escapeXml(role)}"
            aria-label="${escapeXml(
              compare
                ? compareCellAriaLabel(cell, bCell, metric, anatomy, compare)
                : cellAriaLabel(cell, metric, anatomy),
            )}"/>`;
          if (!annotate) return rect;

          const label = cellLabel(cell, metric);
          if (!compare) {
            if (label === undefined) return rect;
            return (
              rect +
              cellAnnotation({ x, y }, paint.ink, palette.fontMono, [
                {
                  text: label,
                  dx: CELL / 2,
                  dy: CELL / 2 + (CELL_LABEL_FONT * CAP_HEIGHT_EM) / 2,
                  size: CELL_LABEL_FONT,
                  anchor: "middle",
                },
              ])
            );
          }

          const bLabel = bCell ? cellLabel(bCell, metric) : undefined;
          // Neither side has anything to say: leave the cell bare, which is what
          // an unlabelled cell inside a labelled grid already means here — "not
          // known". Two em dashes would spend both baselines saying it twice.
          if (label === undefined && bLabel === undefined) return rect;
          return (
            rect +
            cellAnnotation({ x, y }, paint.ink, palette.fontMono, [
              {
                text: label ?? NO_VALUE,
                dx: COMPARE_LABEL_INSET,
                dy: COMPARE_A_BASELINE,
                size: COMPARE_LABEL_FONT,
                anchor: "start",
                variant: "a",
              },
              {
                text: bLabel ?? NO_VALUE,
                dx: CELL - COMPARE_LABEL_INSET,
                dy: COMPARE_B_BASELINE,
                size: COMPARE_LABEL_FONT,
                anchor: "end",
                variant: "b",
              },
            ])
          );
        })
        .join("");

      return label + cells;
    })
    .join("");

  // No `role="grid"` on a band. The map is three documents now and it is still
  // one grid: `ui/anatomy-map.ts` puts the role on the element that wraps all
  // three, which is the contract `enhanceAnatomyMap` reads — a row is a
  // `data-role`, not a parent node, so a row split across two bands still
  // navigates as one row. The label band is marked decorative because it holds
  // only the role names every cell already carries in its own `aria-label`.
  const decorative = band === "labels" ? ` aria-hidden="true"` : "";

  return `<svg viewBox="0 0 ${num(width)} ${num(height)}" width="${num(width)}" height="${num(height)}"${decorative}>
        ${rows}
        ${renderMarginalStrip(options, band, columns, width, height, palette)}
        ${renderBandAxis(band, columns, width, height, palette)}
      </svg>`;
}

/* -- card ------------------------------------------------------------------ */

/**
 * The model's headline totals.
 *
 * Cells whose parameter count could not be resolved contribute **neither**
 * bytes nor params. Counting their bytes against a params total that excludes
 * them would inflate the headline bits-per-weight by exactly the share of the
 * model we could not pin down — a confident wrong number, which is the failure
 * `anatomy.ts` already refuses at the cell level. `anatomy.ambiguousCount`
 * carries the omission to the reader.
 */
export function anatomyTotals(anatomy: Anatomy): AnatomyTotals {
  let params = 0;
  let weightBytes = 0;
  let bufferBytes = 0;
  let unresolvedBytes = 0;
  for (const cell of anatomy.cells.values()) {
    /*
     * Buffers first, because they are the one kind of zero-parameter cell whose
     * bytes must survive the skip below.
     *
     * A causal mask has no parameters *and* real bytes. The old code's single
     * `params <= 0` skip would have dropped both, which reads as a fix — gpt2's
     * parameter count comes right — while quietly making the byte total 475 MiB
     * against a 523 MiB file. That is worse than the defect it replaces: the
     * parameter count is something a reader has to trust us for, but the file
     * size is one click away on the Hub, so a number that disagrees with it
     * discredits every number beside it.
     */
    if (cell.isBuffer) {
      bufferBytes += cell.bytes;
      continue;
    }
    /*
     * The other zero-parameter cell: a payload whose parameter count we refuse
     * to invent — an unresolvable quantized triple, or EXL3, which is
     * recognized and declined.
     *
     * **Its bytes belong to the file total, and an earlier version of this
     * comment argued they did not.** The argument was that adding them "would
     * assert a completeness we do not have", and it confused two different
     * claims: the byte figure answers *how much do I download*, which is known
     * exactly from the header whether or not anything can be decoded from it.
     * Refusing to count a parameter is honest; refusing to count a byte we can
     * see is just a wrong size.
     *
     * It cost nothing while the only zero-parameter cells were small. EXL3 is
     * where it stops being academic — the trellis payload is 18 GB of a 21.6 GB
     * checkpoint, so excluding it reported **5.23 GiB** for a file the Hub
     * lists at 20.1 GiB.
     */
    if (cell.params <= 0) {
      unresolvedBytes += cell.bytes;
      continue;
    }
    params += cell.params;
    /*
     * Only the bytes this cell can account for.
     *
     * A cell can hold both kinds at once: `mlp.down` on the EXL3 checkpoint has
     * an undecoded `I16` trellis beside a real `BF16` bias. Charging the
     * trellis's bytes to the bias's parameters read **231.81 bits/weight** in
     * that cell and 46.13 in the headline — the same error as charging buffer
     * bytes to weights, one kind of zero-parameter tensor over.
     *
     * The remainder is not discarded, it moves to `unresolvedBytes`, so the
     * file total still closes.
     */
    weightBytes += cell.bytes - cell.unresolvedBytes;
    unresolvedBytes += cell.unresolvedBytes;
  }
  return {
    params,
    weightBytes,
    bufferBytes,
    unresolvedBytes,
    bytes: weightBytes + bufferBytes + unresolvedBytes,
    /*
     * Weight bytes over weight params — the true per-weight cost.
     *
     * Dividing the *file* bytes by parameters instead would report gpt2 at
     * 35.24 bits/weight for a checkpoint that is F32 from end to end, because
     * 48 MiB of mask would be charged to weights that do not hold it. That
     * number is not wrong by a rounding error; it is not a per-weight cost at
     * all, and it is the reason this function returns four figures rather than
     * letting each caller divide whichever pair it has to hand.
     */
    effectiveBpw: params > 0 ? (weightBytes * 8) / params : 0,
  };
}

/**
 * The four figures, and the identity that binds them.
 *
 * `bytes === weightBytes + bufferBytes`, `params` counts weights only, and
 * `effectiveBpw` is `weightBytes × 8 ÷ params`. Owner's convention, 2026-08-17:
 * params answers "how many parameters does this model have", the byte total
 * stays reconcilable with the Hub's file size, and bits/weight stays a cost per
 * weight. Any two of the three can be made to agree by accident; all four
 * together cannot, which is why they are computed in one place.
 */
export interface AnatomyTotals {
  /** Learned parameters. Buffers excluded; see `analysis/buffers.ts`. */
  readonly params: number;
  /** Bytes backing those parameters. */
  readonly weightBytes: number;
  /** Bytes held by non-parameter buffers — masks, rotary frequencies. */
  readonly bufferBytes: number;
  /**
   * Bytes whose parameter count is unknown — an unresolvable quantized triple,
   * or a packed format recognized and declined. Real bytes, unknown weights.
   */
  readonly unresolvedBytes: number;
  /** `weightBytes + bufferBytes + unresolvedBytes`: what the reader downloads. */
  readonly bytes: number;
  /** `weightBytes × 8 ÷ params`. */
  readonly effectiveBpw: number;
}

/**
 * The three global statistics, for whatever the anatomy covers.
 *
 * **One function, two surfaces.** The share card prints this under its tower
 * line; the app prints the same string in the anatomy panel's header, so a
 * visitor arriving from a shared link reads the identical figures in the
 * identical order. They were separate implementations — `cardStats` in
 * `og/src/card.ts` — which is one edit away from the two disagreeing about a
 * model, and this project's whole claim is that its numbers come from the file.
 *
 * Scope is **whatever `anatomy` was built from**, which since OG-SPEC §12.1 is
 * one tower. That is deliberate and it is why this function refuses to name a
 * scope itself: §12.4 permits tower statistics *or* whole-checkpoint ones and
 * requires only that the scope be explicit, so the caller that knows which it
 * has is the caller that must label it. On both surfaces today that label is
 * the tower line directly above.
 *
 * Bytes as well as params, unlike `cardHeadline` — a reader wants to know the
 * download, and it is the statistic §12.4 lists first.
 *
 * ## `of`, and why only one surface passes it
 *
 * Given `of`, each figure says what it is a share of — `25.6B params (of
 * 27.4B) · 12.2 GiB (of 13.7 GiB)`. That answers the tower-versus-checkpoint
 * question in the figures themselves rather than in prose beside them, which
 * is the clearest form §12.4's "be explicit about scope" can take.
 *
 * The card does not pass it and should not: at 1200px the line already shares a
 * baseline with the legend, and `subtitleRight` exists because a long one
 * collides. The page has the width; the card does not (owner, 2026-08-10).
 *
 * Omitted per-figure when a total is absent or not larger than the part — on a
 * single-stack model the tower *is* the checkpoint, and `(of 27.4B)` beside
 * `27.4B` is noise.
 */
export function anatomyStatsLine(
  anatomy: Anatomy,
  of?: { readonly params: number | undefined; readonly bytes: number | undefined },
): string {
  return anatomyStats(anatomy, of)
    .map((s) => (s.of ? `${s.value} ${s.of}` : s.value))
    .join(" · ");
}

/**
 * One statistic, split into the parts a caller may want to style differently.
 *
 * The card renders text into SVG and joins these back into a string; the page
 * wraps each part in its own element, so the totals can recede and the headline
 * figure can carry weight. Splitting here rather than at each call site is what
 * keeps the two surfaces from computing the same figures twice, which is the
 * failure `anatomyStatsLine` exists to prevent.
 */
export interface StatSegment {
  /** The measurement itself, e.g. `25.6B params`. */
  readonly value: string;
  /** `(of 27.4B)`, when a total was supplied and exceeds the part. */
  readonly of: string | undefined;
  /** The one figure worth emphasising: what a weight actually costs. */
  readonly lead?: boolean;
}

/** `anatomyStatsLine`'s parts, before they are joined. */
export function anatomyStats(
  anatomy: Anatomy,
  of?: { readonly params: number | undefined; readonly bytes: number | undefined },
): StatSegment[] {
  const totals = anatomyTotals(anatomy);
  if (totals.params <= 0) {
    return [{ value: "parameter count not resolvable from metadata", of: undefined }];
  }
  const share = (
    part: number,
    whole: number | undefined,
    format: (n: number) => string,
  ) => (whole !== undefined && whole > part ? `(of ${format(whole)})` : undefined);
  return [
    {
      value: `${formatParams(totals.params)} params`,
      of: share(totals.params, of?.params, formatParams),
    },
    {
      value: formatBytes(totals.bytes),
      of: share(totals.bytes, of?.bytes, formatBytes),
    },
    // No total, deliberately: it is a ratio, so "(of 4.30)" would read as a
    // fraction of something rather than as this tower against the model.
    {
      value: `${totals.effectiveBpw.toFixed(2)} bits/weight`,
      of: undefined,
      lead: true,
    },
  ];
}

/** The params + effective-bpw headline plan §23 puts under the model id. */
export function cardHeadline(anatomy: Anatomy): string {
  const totals = anatomyTotals(anatomy);
  const parts =
    totals.params > 0
      ? [
          `${formatParams(totals.params)} params`,
          `${totals.effectiveBpw.toFixed(2)} bits/weight`,
        ]
      : ["parameter count not resolvable from metadata"];
  if (anatomy.ambiguousCount > 0) {
    parts.push(
      `${anatomy.ambiguousCount} tensor${
        anatomy.ambiguousCount === 1 ? "" : "s"
      } unresolved`,
    );
  }
  return parts.join(" · ");
}

/**
 * Truncate to what fits, from the advance approximation rather than a measured
 * width.
 *
 * Rounds the available character count *down*, so the estimate erring low costs
 * an unnecessary ellipsis rather than text running off the card.
 */
function fitMono(text: string, fontSize: number, available: number): string {
  const max = Math.floor(available / (fontSize * MONO_ADVANCE_EM));
  if (max <= 1) return "…";
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

/**
 * Greedy word wrap, against the same advance approximation everything else here
 * uses.
 *
 * There was no wrapper before the sentence card, and that was not an oversight:
 * every other string on a card is one line that is either truncated (`fitMono`)
 * or anchored to an edge, because a card cannot reflow and a second line is a
 * layout decision rather than an accident. The sentence is the one string whose
 * whole job is to be read as prose.
 *
 * `approxWidth` over-reserves for sans (see `SANS_ADVANCE_EM`), so the error
 * falls in the safe direction: a break one word early, never a line running off
 * the card. A single word wider than the box is emitted on its own line rather
 * than dropped — the model ids that could do that are already truncated by
 * `fitMono` two rows above, and silently losing a word from a sentence is worse
 * than one over-long line.
 */
function wrapText(content: string, size: number, maxWidth: number): readonly string[] {
  const lines: string[] = [];
  let line = "";
  for (const word of content.split(/\s+/).filter(Boolean)) {
    const next = line ? `${line} ${word}` : word;
    if (line && approxWidth(next, size) > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** A `<text>` element with everything a rasterizer needs stated explicitly. */
function text(
  content: string,
  attrs: {
    readonly x: number;
    readonly y: number;
    readonly size: number;
    readonly fill: string;
    readonly font: string;
    readonly weight?: number;
    readonly anchor?: "start" | "middle" | "end";
    readonly opacity?: number;
  },
): string {
  const anchor = attrs.anchor ? ` text-anchor="${attrs.anchor}"` : "";
  const weight = attrs.weight ? ` font-weight="${attrs.weight}"` : "";
  const opacity = attrs.opacity === undefined ? "" : ` opacity="${num(attrs.opacity)}"`;
  return `<text x="${num(attrs.x)}" y="${num(attrs.y)}" font-size="${num(attrs.size)}" font-family="${escapeXml(attrs.font)}" fill="${attrs.fill}"${weight}${anchor}${opacity}>${escapeXml(content)}</text>`;
}

/**
 * A small `↳` drawn as strokes, for the tower's detail line.
 *
 * `size` is the type size it sits beside; the elbow is scaled from that so the
 * two stay in proportion at any card size. `y` is the text baseline it aligns
 * to, which is why the geometry is expressed as offsets above it.
 */
function elbow(x: number, y: number, size: number, color: string): string {
  const h = size * 0.52;
  const w = size * 0.46;
  const top = y - size * 0.66;
  const stroke = Math.max(1, size * 0.075);
  const head = size * 0.17;
  const tipX = x + w;
  const tipY = top + h;
  const line = `fill="none" stroke="${color}" stroke-width="${num(stroke)}" stroke-linecap="round" stroke-linejoin="round"`;
  return `<path d="M ${num(x)} ${num(top)} L ${num(x)} ${num(tipY)} L ${num(tipX)} ${num(tipY)}" ${line}/><path d="M ${num(tipX - head)} ${num(tipY - head * 0.72)} L ${num(tipX)} ${num(tipY)} L ${num(tipX - head)} ${num(tipY + head * 0.72)}" ${line}/>`;
}

/** The wordmark, split where its colour changes. */
const WORDMARK = "TensorLens";

/** What the tower line opens with; also the base of the statistics' indent. */
const TOWER_LINE_PREFIX = "Towers: ";

/**
 * What the part line opens with.
 *
 * "Part" rather than "Quantization" or "Variant" because the same row serves a
 * GGUF quant and a safetensors component, and it is the word the page's own
 * line uses — someone arriving from a shared link should meet the same noun.
 */
const PART_LINE_PREFIX = "Part: ";

/**
 * Roughly how wide `content` will be, for placing something *beside* text.
 *
 * Never for deciding where an edge lands — `text-anchor` does that exactly. See
 * `SANS_ADVANCE_EM` for why this is allowed to be approximate.
 */
function approxWidth(content: string, size: number): number {
  return content.length * size * SANS_ADVANCE_EM;
}

/**
 * The same, for monospace — where it is not an approximation at all, since
 * every glyph advances by exactly `MONO_ADVANCE_EM`.
 */
function approxMonoWidth(content: string, size: number): number {
  return content.length * size * MONO_ADVANCE_EM;
}

/**
 * One line of text whose runs differ in weight or colour.
 *
 * `text` escapes its content, which is the right default and makes it unable to
 * carry the tower line: that needs one run bold and full-ink and the rest muted,
 * on a single baseline. Separate `<text>` elements would need advance-width
 * arithmetic to place, and this file's `MONO_ADVANCE_EM` approximation exists
 * because that arithmetic is only ever approximate. `<tspan>` lets the
 * rasterizer do the layout, which it can do exactly.
 *
 * Every segment is still escaped individually, so this widens what can be
 * *styled*, not what can be *injected*.
 */
function richText(attrs: {
  readonly x: number;
  readonly y: number;
  readonly size: number;
  readonly font: string;
  readonly anchor?: "start" | "middle" | "end";
  readonly segments: ReadonlyArray<{
    readonly content: string;
    readonly fill: string;
    readonly weight?: number;
    readonly underline?: boolean;
  }>;
}): string {
  const spans = attrs.segments
    .map((segment) => {
      const weight = segment.weight ? ` font-weight="${segment.weight}"` : "";
      // Weight alone was not enough to mark the selected tower at card size
      // (owner, 2026-08-08); an underline reads at a glance and at thumbnail
      // scale, where a bold/regular difference does not.
      const underline = segment.underline ? ` text-decoration="underline"` : "";
      return `<tspan fill="${segment.fill}"${weight}${underline}>${escapeXml(segment.content)}</tspan>`;
    })
    .join("");
  const anchor = attrs.anchor ? ` text-anchor="${attrs.anchor}"` : "";
  return `<text x="${num(attrs.x)}" y="${num(attrs.y)}" font-size="${num(attrs.size)}" font-family="${escapeXml(attrs.font)}"${anchor}>${spans}</text>`;
}

/**
 * The lens mark as a card watermark — the third standalone context for this
 * geometry, after `ui/logo.ts` (inline, inherits page tokens) and
 * `public/favicon.svg` (a separate document with its own hexes). All three
 * carry the same lens ring and the same bell-plus-outlier, and all three must
 * be changed together.
 *
 * Two differences from the inline version, both forced by the consumers here:
 *
 * - **No `clipPath`, so no id.** The inline logo clips its bars to the ring
 *   through a randomly-named `clipPath`; random defeats the determinism a
 *   golden-PNG test needs, and a fixed id is exactly what collides when a card
 *   is embedded. Only the baseline rule actually crosses the ring, so it is cut
 *   to the ring's chord arithmetically instead: at the rule's lower edge
 *   `y = 23.9` the circle (cx 16, cy 16, r 12.4) is `√(12.4² − 7.9²) ≈ 9.56`
 *   wide either side of centre. Cutting at the *lower* edge keeps the whole
 *   rule inside the ring — a hair shorter than a true clip, invisible at
 *   watermark opacity.
 * - **Colour arrives resolved**, for the same reason everything else does.
 *
 * Exported because the og package's static fallback card (plan §23.2 — an
 * unknown repo gets the generic card, never an error image) shows the mark as
 * its subject rather than as a watermark, and a *fourth* transcription of this
 * geometry is exactly what CLAUDE.md's "the logo is duplicated on purpose"
 * note is trying to keep from happening by accident. Opacity is already a
 * parameter, so foreground use needs nothing else.
 */
export function lensWatermark(
  x: number,
  y: number,
  size: number,
  color: string,
  opacity: number,
): string {
  const scale = size / 32;
  const half = Math.sqrt(12.4 * 12.4 - 7.9 * 7.9);
  const bars = [
    { x: 7.5, y: 16.5, w: 3, h: 7.4, r: 1 },
    { x: 11.5, y: 10.5, w: 3, h: 13.4, r: 1 },
    { x: 15.5, y: 8, w: 3, h: 15.9, r: 1 },
    { x: 19.5, y: 14, w: 3, h: 9.9, r: 1 },
    { x: 23, y: 20.2, w: 2.2, h: 3.7, r: 1 },
    { x: 16 - half, y: 22.6, w: half * 2, h: 1.3, r: 0 },
  ]
    .map(
      (b) =>
        `<rect x="${num(b.x)}" y="${num(b.y)}" width="${num(b.w)}" height="${num(b.h)}"${b.r ? ` rx="${num(b.r)}"` : ""}/>`,
    )
    .join("");
  return `<g transform="translate(${num(x)} ${num(y)}) scale(${num(scale)})" fill="${color}" opacity="${num(opacity)}"><g>${bars}</g><circle cx="16" cy="16" r="12.4" fill="none" stroke="${color}" stroke-width="2.2"/></g>`;
}

/**
 * The share card: bpw-shaded grid, model id, headline, watermark (plan §23).
 *
 * **No cell annotations** — at 1200×630 in a social timeline the grid is read
 * as a shape, not a table, and per-cell text at that scale is noise that also
 * multiplies the font-substitution risk. The legend endpoints stay, because the
 * ramp is relative to this model: shading without stated endpoints would be a
 * picture with no units.
 */
function renderCardSvg(options: MapRenderOptions): string {
  const { anatomy, metric, width, height, palette } = options;
  const shading = shadingFor(anatomy, metric, palette);

  const pad = Math.round(Math.min(width, height) * 0.08);
  /*
   * Vertical margin, tighter than the horizontal one (owner, 2026-08-08).
   *
   * A 1200x630 canvas is far wider than it is tall, so a single square margin
   * spends proportionally much more of the scarce axis. Pulling the top and
   * bottom in reclaims ~24px, which goes to the identity lockup below; the left
   * and right stay at `pad`, where the long model id and the wide grid want the
   * breathing room.
   *
   * Tightened again from 0.06 (owner, 2026-08-08), to pay for the layer axis's
   * strip below. Reserving that strip out of the grid box costs the map ~21px
   * of height, and taking it from the margins instead means the fix for the
   * overlap does not also shrink the thing the card exists to show.
   */
  const padY = Math.round(Math.min(width, height) * 0.045);
  /*
   * The type scale, tuned by rendering real models (owner, 2026-08-08).
   *
   * The +2 on the title and the tower line reading larger than the statistics
   * are both deliberate: the tower line says *what the map is*, which governs
   * everything under it, while the statistics are the detail you read second.
   * They were the same size and the reading order was ambiguous.
   */
  const titleSize = Math.round(height * 0.055) + 2;
  const towerSize = Math.round(height * 0.034);
  const subtitleSize = Math.round(height * 0.03);
  const footSize = Math.round(height * 0.026);

  const titleY = padY + titleSize;
  // The tower line, when present, takes a row between the id and the
  // statistics; everything below shifts by exactly its height, so a card
  // without one lays out precisely as it did before.
  const towerLine = options.towerLine;
  const partLine = options.partLine;
  const repoLine = options.repoLine;
  /*
   * The header is a stack of optional rows, laid out in sequence.
   *
   * It used to be a two-link chain and adding a third would have made the
   * offsets a nested ternary — `(tower ? towerY : part ? partY : titleY)` —
   * which is the shape that gets one branch wrong the next time a row is added.
   * Each row here takes the baseline the previous one left.
   */
  const repoY = repoLine ? titleY + Math.round(towerSize * 1.5) : titleY;
  const partY = partLine ? repoY + Math.round(towerSize * 1.5) : repoY;
  const towerY = towerLine ? partY + Math.round(towerSize * 1.5) : partY;
  const subtitleY = towerY + Math.round(subtitleSize * 1.7);

  /*
   * Indent the statistics to sit under the selected tower, behind a `↳`, so the
   * line reads as detail folding out of that choice rather than as a second
   * caption about the whole model (owner, 2026-08-08).
   *
   * The x is measured up to the *middle* of the selected label, which is what
   * makes the arrow point at the word rather than at the gap before it. It is
   * approximate — see `SANS_ADVANCE_EM` — and approximate is fine here: nothing
   * aligns to it, and a few pixels either way still reads as "under Text".
   *
   * Zero when there is no tower line, so a single-stack card keeps the
   * statistics hard left where they belong.
   */
  const subtitleIndent = (() => {
    if (!towerLine) return 0;
    let x = approxWidth(TOWER_LINE_PREFIX, towerSize);
    for (const tower of towerLine) {
      // The elbow's stem lands under the label's **first letter** (owner,
      // 2026-08-08). Mid-word was the first attempt and read as detached; the
      // first letter is also the rule that generalises — it holds whatever the
      // tower is called and however long the name is, where a centre offset
      // depends on the width of a word nobody chose.
      if (tower.selected) return x;
      x += approxWidth(`${tower.label} · `, towerSize);
    }
    return 0;
  })();
  const footY = height - padY;

  /*
   * The legend sits top-right, and the bottom-left of a share card is a
   * keep-clear zone.
   *
   * X overlays a rounded chip carrying the page title across the bottom-left
   * corner of a `summary_large_image` card. It is drawn by the client, over our
   * pixels, and no card markup can move or suppress it. Reported with a
   * screenshot of the Kimi-K3 card, 2026-08-09: the chip covered the legend
   * completely, so the one line that says what the shading *means* was the one
   * line a reader could not see. The map was still a picture with no units — the
   * exact failure the legend exists to prevent, arriving by a route the renderer
   * cannot detect.
   *
   * So this is a constraint on the design, not a fix to one card: anything
   * informational stays out of the bottom-left. What remains down there is the
   * watermark arc, which is texture, and the layer axis, which sits above the
   * chip. The identity lockup is bottom-*right* and unaffected.
   *
   * Top-right is the space that was already empty — the header block is
   * left-aligned, so the right half of those three lines has never held
   * anything. The legend keys the shading, so it belongs beside the statistics
   * rather than beside the model id: same baseline as the subtitle, right edge
   * flush with the margin the lockup and the tagline already align to.
   */
  const subtitle = options.subtitle ?? cardHeadline(anatomy);
  const legendModelForCard = legendModel(anatomy, metric, palette);

  /*
   * §12.5's amendment: when the stack is not a whole model, a sentence saying so
   * takes the map's place — and takes the legend and the layer axis with it.
   *
   * The legend keys the *shading*, and with no cells painted there is no shading
   * to key. Leaving it is a stronger claim than dropping it: a scale printed
   * beside nothing sends a reader looking for the cells it describes. The axis
   * goes for the same reason — `L0` under empty space labels an axis that is not
   * there.
   *
   * Everything above stays exactly where it was. The id, the tower line and the
   * statistics are the same rows in the same places as on a map card, so the
   * only difference a reader meets is what the card's middle says.
   */
  const sentence = options.insteadOfMap;

  /*
   * Where the subtitle ends, so the legend can be told to get out of its way.
   *
   * Approximate on the safe side — `approxWidth` over-reserves for sans — and
   * the failure it guards is real: a long statistics line and a six-swatch dtype
   * legend can both want the same band. When they do, the legend takes its own
   * line underneath rather than overprinting, and the grid starts that much
   * lower. Costing the map one line of height in the rare case is the right
   * trade against two texts on top of each other in it.
   */
  const subtitleRight =
    pad +
    subtitleIndent +
    (subtitleIndent > 0 ? Math.round(subtitleSize * 1.15) : 0) +
    approxWidth(subtitle, subtitleSize);
  const legendRight = width - pad;
  const onSubtitleLine = layoutCardLegend(legendModelForCard, palette, {
    right: legendRight,
    y: subtitleY,
    size: footSize,
  });
  const legendOwnLine = !sentence && onSubtitleLine.left < subtitleRight + footSize;
  const legendBaselineY = legendOwnLine
    ? subtitleY + Math.round(footSize * 1.6)
    : subtitleY;
  const legend = sentence
    ? ""
    : legendOwnLine
      ? layoutCardLegend(legendModelForCard, palette, {
          right: legendRight,
          y: legendBaselineY,
          size: footSize,
        }).svg
      : onSubtitleLine.svg;

  /*
   * The grid starts below whichever header line comes last — normally the
   * subtitle, and the legend when a collision pushed it onto its own.
   */
  const gridTop = legendBaselineY + Math.round(subtitleSize * 1.1);

  /*
   * The layer axis is drawn *below* the grid, so its strip has to come out of
   * the grid's box — not out of whatever happens to be under it.
   *
   * It did not, when the axis was added. `gridBottom` still described a box
   * that ended where the map ended, and the labels were then placed a further
   * `axisSize * 1.5` down from there. On a deep model the grid fills its box,
   * so that offset landed the labels inside the band holding the legend and the
   * identity lockup: at 13 roles the right-hand ticks sat on top of the
   * TensorLens mark, which is bled to that corner and cannot move.
   *
   * 1.5 for the baseline, plus a descender's worth so a label cannot touch what
   * is under it.
   */
  const axisSize = Math.max(9, Math.round(footSize * 0.72));
  // Reserved out of the grid's box, so it is only owed when an axis is drawn —
  // a sentence card gets those pixels back as room to be read in.
  const axisStrip = sentence ? 0 : Math.round(axisSize * 1.8);

  /*
   * The identity lockup's measurements, kept next to each other so the three
   * pieces cannot drift apart — and computed *here*, above the grid, because
   * the grid's floor depends on them.
   *
   * The wordmark is a quarter larger than it was (owner, 2026-08-08): the
   * lockup is the card's signature and was reading as a footnote next to a 37px
   * model id. Its baseline then sat `footSize * 1.9` above the tagline, which
   * is more air than a two-line lockup needs; at 1.45 the two lines read as one
   * unit and the whole lockup drops ~7px, which is 7px the map gets to keep
   * (owner, 2026-08-08).
   */
  const wordmarkSize = Math.round(footSize * 1.5 * 1.25);
  const wordmarkY = footY - Math.round(footSize * 1.45);
  const markSize = Math.round(wordmarkSize * 1.35);
  const markGap = Math.round(wordmarkSize * 0.42);
  // Mirrors the `markY` the lockup is actually drawn at, below — the mark is
  // centred on the wordmark's cap band, and is the tallest thing down there.
  const lockupTop = wordmarkY - wordmarkSize * CAP_HEIGHT_EM * 0.5 - markSize / 2;

  /*
   * The floor is the identity lockup, and now it is the only thing down there.
   *
   * This used to take the min of the lockup and the legend, because the legend
   * was the obvious constraint and the lockup was the one that actually bound —
   * the mark is half again the height of a legend line and its top edge sat
   * ~26px higher, so a grid clearing the legend comfortably could still run into
   * the mark. With the legend moved to the header band that min has one term,
   * and the term left is the one that was deciding anyway. The map's height does
   * not change by moving the legend; only what can overprint it does.
   */
  const gridBottom = lockupTop - footSize * 0.5 - axisStrip;

  const gridHeight = Math.max(1, gridBottom - gridTop);
  const cols = Math.max(1, anatomy.layers.length);
  const rows = Math.max(1, anatomy.roles.length);

  /*
   * Spec §12.3: horizontal and vertical pitch are **independent**.
   *
   * One pitch for both axes keeps cells square, and square was defensible while
   * the deepest card anybody had rendered was 32 layers. It does not survive the
   * range the spec asks for — 2 to 150. `moonshotai/Kimi-K3` is 94 layers and 12
   * roles: the width allows ~10.5px per column, a square grid takes that for the
   * rows too, and the map collapses into a 130px band adrift in a 370px box,
   * with two thirds of the card's scarcest axis spent on nothing.
   *
   * Worse, and the reason this is a defect and not a preference: the role labels
   * are sized from `footSize`, so a 16px label was being placed on a 10.5px row.
   * `embed`, `attn.q`, `attn.k` overprinted each other into an unreadable smear.
   * The map went on rendering, which is how it reached a card at all.
   *
   * At 150 layers this yields ~5–6px columns — the "narrow heat-map pixels" the
   * spec calls for — while the cap keeps a 2-layer model from stretching two
   * columns across the whole card.
   */
  const pitchY = Math.min(gridHeight / rows, MAX_PITCH);

  /*
   * The label follows the row it names, so it cannot outgrow it. `0.78` leaves
   * the ascender and descender room inside the pitch; the floor of 8 is the
   * point below which mono stops being legible in a rasterized PNG at all, and
   * a card that deep has other problems.
   *
   * Sized before the gutter, which is sized before the grid — over-reserving the
   * gutter costs a few pixels of whitespace, under-reserving clips a role name.
   * No cycle: `pitchY` depends on the grid's *height*, and the gutter only moves
   * its left edge.
   */
  const roleLabelSize = Math.max(8, Math.min(footSize, 18, Math.floor(pitchY * 0.78)));
  const longestRole = anatomy.roles.reduce((n, r) => Math.max(n, r.length), 0);
  const gutter =
    anatomy.roles.length === 0
      ? 0
      : Math.ceil(Math.min(longestRole, 14) * roleLabelSize * MONO_ADVANCE_EM) + 12;

  const gridLeft = pad + gutter;
  const gridWidth = Math.max(1, width - pad - gridLeft);
  const pitchX = Math.min(gridWidth / cols, MAX_PITCH);

  // A proportional gap on each axis rather than one shared figure: a single gap
  // taken from the smaller pitch would leave 1.5px between 30px-tall rows, and
  // the rows would read as continuous bars rather than as cells.
  const gapX = Math.max(1, pitchX * 0.14);
  const gapY = Math.max(1, pitchY * 0.14);
  const cellW = Math.max(1, pitchX - gapX);
  const cellH = Math.max(1, pitchY - gapY);
  const radius = Math.min(5, Math.min(cellW, cellH) * 0.18);

  // Vertically centred in whatever the grid did not use.
  const originY = gridTop + (gridHeight - (rows * pitchY - gapY)) / 2;

  /*
   * Horizontally centred too, once the map is narrower than the space it has.
   *
   * `pitchX` is capped, so a model with few layers cannot grow to fill the
   * width — gpt2's twelve columns used a little over half of it and sat hard
   * left, with the watermark alone in the empty half. That reads as a layout
   * accident rather than as a small model.
   *
   * The row labels hang off `gridLeft`, so the whole unit — labels and grid —
   * moves together. `max(0, …)` because a map that fills or overflows its width
   * must stay exactly where it is: shifting it would push the labels off the
   * card.
   */
  const usedWidth = cols * pitchX - gapX;
  const originX = gridLeft + Math.max(0, (gridWidth - usedWidth) / 2);

  const cellMarks = anatomy.roles
    .map((role, rowIndex) => {
      const y = originY + rowIndex * pitchY;
      const label = text(fitMono(role, roleLabelSize, gutter), {
        x: originX - 8,
        y: y + cellH / 2 + (roleLabelSize * CAP_HEIGHT_EM) / 2,
        size: roleLabelSize,
        fill: palette.inkMuted,
        font: palette.fontMono,
        anchor: "end",
      });
      const marks = anatomy.layers
        .map((layer, colIndex) => {
          const found = anatomy.cells.get(cellKey(layer, role));
          // Absent, not empty — the ragged edge is the finding.
          if (!found) return "";
          const x = originX + colIndex * pitchX;
          return `<rect x="${num(x)}" y="${num(y)}" width="${num(cellW)}" height="${num(cellH)}" rx="${num(radius)}" fill="${paintFor(found, shading).fill}"/>`;
        })
        .join("");
      return label + marks;
    })
    .join("");

  /*
   * A layer axis under the map (owner, 2026-08-08).
   *
   * Without it the columns are an unlabelled run of squares and a reader has to
   * infer what the horizontal direction means. The interactive map says
   * `layer →` beside numbered ticks; the card has no room for a caption beside
   * the grid, so the tick labels carry it themselves — `L1`, `L5` — where the
   * `L` is doing the caption's job in one character.
   *
   * Layer numbers are the model's own indices, so a stack that starts at 0
   * reads `L0`. Renumbering them from 1 to look tidier would put a label on the
   * card that matches nothing in the tensor names it describes.
   *
   * Labels go on layers whose **number** is a round multiple, which is the rule
   * `renderTickStrip` already uses for the interactive map (`layer % 5`). The
   * card derived a stride from the pitch instead, and on a 32-layer model that
   * came out at 2 — so the same model read `L0 L2 L4 …` on the card and
   * `0 5 10 …` on the site (owner, 2026-08-08). Two labellings of one axis, and
   * the denser one was also the noisier one.
   *
   * The pitch still gets a say, but only to thin: at 150 layers every fifth
   * label would overprint, so the step widens to the next round multiple that
   * fits. It never tightens below 5, because "every 5th layer" is the thing a
   * reader is being taught to expect.
   */
  const axisLabelWidth = axisSize * MONO_ADVANCE_EM * 4;
  const axisStep =
    [5, 10, 25, 50].find((step) => step * pitchX >= axisLabelWidth) ?? 50;
  const axisY = originY + rows * pitchY - gapY + axisSize * 1.5;

  /*
   * `global` is six characters over a column that may be six pixels wide, so it
   * is set smaller and is the one label allowed to overhang its own column — it
   * sits at the end of the axis with the card's margin to its right.
   *
   * What it may not do is collide with the last numeric tick. On Kimi-K3 the
   * step is 5 and the global column is three past L90, which at a 10px pitch
   * left the two labels touching: `L90global`. So the numeric tick yields — the
   * named column is the one carrying information a reader cannot reconstruct,
   * where a missing `L90` is still implied by L85 and the step.
   */
  const globalSize = Math.max(8, axisSize - 2);
  const globalIndex = anatomy.layers.indexOf(undefined);
  const globalLeft =
    globalIndex < 0
      ? Number.POSITIVE_INFINITY
      : originX +
        globalIndex * pitchX +
        cellW / 2 -
        approxMonoWidth(GLOBAL_LABEL, globalSize) / 2 -
        globalSize * 0.5;

  const axisMarks = anatomy.layers
    .map((layer, index) => {
      // `layers` ends with `undefined` for the non-stack column — the embedding
      // and the final norm, which have no layer index. It rendered as
      // "Lundefined"; the interactive strip names it `global`, and the card now
      // does the same (owner, 2026-08-08). Leaving it blank was the fix for the
      // tofu, but it left the card's one genuinely different column looking like
      // a layer whose label had simply been thinned away by the stride.
      const isGlobal = layer === undefined;
      if (!isGlobal && (layer as number) % axisStep !== 0) return "";

      const size = isGlobal ? globalSize : axisSize;
      const label = isGlobal ? GLOBAL_LABEL : `L${layer}`;
      const centre = originX + index * pitchX + cellW / 2;
      if (!isGlobal && centre + approxMonoWidth(label, size) / 2 > globalLeft)
        return "";

      return text(label, {
        x: centre,
        y: axisY,
        size,
        fill: palette.faint,
        font: palette.fontMono,
        anchor: "middle",
      });
    })
    .join("");

  /*
   * The sentence, occupying the box the grid would have filled.
   *
   * Left-aligned at the same `pad` as every row above it, so the card still has
   * one text edge rather than a centred block floating beside a left-aligned
   * header. Vertically centred in the box, because unlike a grid it does not
   * fill its space and hanging it from the top would leave the card bottom-heavy
   * with watermark.
   *
   * Sized between the model id and the statistics: this is the card's dominant
   * element now, so it outweighs the figures, but it is prose under a title and
   * must not compete with it.
   */
  const sentenceMarks = (() => {
    if (!sentence) return "";
    const size = Math.round(height * 0.05);
    const leading = Math.round(size * 1.38);
    /*
     * Short of the full column on purpose. A line running to the right margin
     * would be ~65 characters at this size, past the measure prose stays
     * readable at, and it would also put the last line of a two-line paragraph
     * directly above the identity lockup's column.
     */
    const lines = wrapText(sentence, size, (width - pad * 2) * 0.82);
    const blockTop =
      gridTop + (gridBottom - gridTop - (lines.length - 1) * leading) / 2;
    return lines
      .map((line, index) =>
        text(line, {
          x: pad,
          // `blockTop` is the top of the text block; a baseline sits a cap
          // height below it, and centring wants the cap band centred rather
          // than the baselines.
          y: blockTop + size * CAP_HEIGHT_EM + index * leading,
          size,
          fill: palette.ink,
          font: palette.fontSans,
        }),
      )
      .join("");
  })();

  // Twice its former 0.46 (owner, 2026-08-08). It is bled off the bottom-right
  // corner rather than inset, so most of the growth happens outside the canvas
  // and what remains on it is a larger arc of the same mark, not a bigger blob.
  const watermarkSize = Math.round(height * 0.92);

  const title = fitMono(options.title ?? "", titleSize, Math.max(1, width - pad * 2));

  // Order is paint order: ground, watermark, grid, then text. The watermark sits
  // *under* the grid so it never competes with a cell for legibility.
  const body = [
    `<rect x="0" y="0" width="${num(width)}" height="${num(height)}" fill="${palette.surface}"/>`,
    lensWatermark(
      /*
       * Mirrored to the bottom-LEFT and weakened (owner, 2026-08-08).
       *
       * The bottom-right is now the logo lockup's corner, and two versions of
       * the same mark in one corner — one as identity, one as texture — reads as
       * a mistake. It also bleeds off both edges by a quarter of its size, so
       * what remains on the canvas is an arc rather than the whole mark: texture
       * that is not asking to be identified.
       */
      pad - watermarkSize / 4,
      height - padY - watermarkSize + watermarkSize / 4,
      watermarkSize,
      palette.accent,
      // Weaker than the 0.07 it was: at this size the old value read as a shape
      // to be identified rather than as texture.
      0.045,
    ),
    sentence ? sentenceMarks : cellMarks,
    sentence ? "" : axisMarks,
    title
      ? text(title, {
          x: pad,
          y: titleY,
          size: titleSize,
          fill: palette.ink,
          font: palette.fontMono,
          weight: 600,
        })
      : "",
    /*
     * The elbow is drawn, not typed.
     *
     * `↳` (U+21B3) rendered as a tofu box: the faces shipped to the rasterizer
     * are subsets, and an arrow is exactly the sort of glyph a Latin subset
     * drops. Nothing in the SVG pipeline warns about it — usvg substitutes
     * silently and the miss only appears in the raster. A path cannot go
     * missing.
     */
    subtitleIndent > 0
      ? elbow(pad + subtitleIndent, subtitleY, subtitleSize, palette.inkMuted)
      : "",
    text(subtitle, {
      x:
        pad +
        subtitleIndent +
        (subtitleIndent > 0 ? Math.round(subtitleSize * 1.15) : 0),
      y: subtitleY,
      size: subtitleSize,
      fill: palette.inkMuted,
      font: palette.fontSans,
    }),
    /*
     * Muted and unprefixed, unlike the two rows below it.
     *
     * Those name a subject and give it a value — "part: Q4_K_M" — because the
     * value is a choice someone made. This row is context for the choice, not
     * another choice, so it recedes: the reading order the card wants is id,
     * then the variant the map is of, with the scope available to anyone who
     * looks rather than competing for the glance.
     */
    repoLine
      ? text(repoLine, {
          x: pad,
          y: repoY,
          size: towerSize,
          fill: palette.inkMuted,
          font: palette.fontSans,
        })
      : "",
    partLine
      ? richText({
          x: pad,
          y: partY,
          size: towerSize,
          font: palette.fontSans,
          segments: [
            // Same subject-then-value shape as the tower line below, so the two
            // read as one block rather than as unrelated captions.
            { content: PART_LINE_PREFIX, fill: palette.inkMuted, weight: 400 },
            { content: partLine, fill: palette.ink, weight: 700 },
          ],
        })
      : "",
    towerLine
      ? richText({
          x: pad,
          y: towerY,
          size: towerSize,
          font: palette.fontSans,
          segments: [
            // A subject for the words. Without it the line reads as a stray
            // pair of nouns under the model id (owner, 2026-08-08).
            { content: TOWER_LINE_PREFIX, fill: palette.inkMuted, weight: 400 },
            ...towerLine.flatMap((tower, index) => [
              ...(index > 0
                ? [{ content: " · ", fill: palette.inkMuted, weight: 400 }]
                : []),
              {
                content: tower.label,
                // The selected stack is the subject of everything below it, so
                // it takes full ink, weight and an underline while the rest
                // stay context.
                fill: tower.selected ? palette.ink : palette.inkMuted,
                weight: tower.selected ? 700 : 400,
                underline: tower.selected,
              },
            ]),
          ],
        })
      : "",
    legend,
    /*
     * The identity block, bottom right (owner, 2026-08-08).
     *
     * The full lockup — mark plus wordmark — rather than the bare domain, so a
     * card lifted out of a feed still says whose it is. `lensWatermark` is the
     * mark's only geometry and its doc comment already anticipates foreground
     * use: opacity is a parameter, so drawing it opaque at logo size needs
     * nothing new and adds no fourth transcription of the shape.
     *
     * The wordmark is anchored `end` at the right margin, so its edge is exact
     * and the tagline beneath lines up with it. The mark's x is the only thing
     * derived from `SANS_ADVANCE_EM`, and an error there moves the gap between
     * mark and word by a pixel or two — which nobody can see, unlike a ragged
     * right edge.
     */
    lensWatermark(
      width - pad - approxWidth(WORDMARK, wordmarkSize) - markGap - markSize,
      // Centred on the wordmark's cap height, not on its baseline — a baseline
      // put the mark a whole line low, on top of the tagline.
      wordmarkY - wordmarkSize * CAP_HEIGHT_EM * 0.5 - markSize / 2,
      markSize,
      palette.accent,
      1,
    ),
    richText({
      x: width - pad,
      y: wordmarkY,
      size: wordmarkSize,
      font: palette.fontSans,
      anchor: "end",
      segments: [
        { content: "Tensor", fill: palette.ink, weight: 700 },
        // "Lens" in the accent — the wordmark's one flourish, matching the site.
        { content: "Lens", fill: palette.accent, weight: 700 },
      ],
    }),
    text("tensorlens.dev · metadata analysis, no weights fetched", {
      x: width - pad,
      y: footY,
      size: footSize,
      fill: palette.faint,
      font: palette.fontSans,
      anchor: "end",
    }),
  ].join("");

  /*
   * The alternative describes what was drawn, not what usually is. A sentence
   * card announced as an "allocation map" would be the one reading where the
   * text alternative and the image disagree — and it is the reading given to
   * anyone who cannot check.
   */
  const described = sentence
    ? `${options.title ?? "A model"}: ${sentence} ${subtitle}`
    : `Allocation map for ${options.title ?? "a model"}: ${subtitle}`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${num(width)} ${num(height)}" width="${num(width)}" height="${num(height)}" role="img" aria-label="${escapeXml(described)}">${body}</svg>`;
}

/**
 * Endpoints, not a full scale.
 *
 * The ramp is relative to this model (see `rampStep`), so the two extremes and
 * the metric's name are the minimum that keeps the shading honest. The dtype map
 * gets swatches instead, capped at six: past that the strip would wrap, and a
 * card cannot reflow. A degenerate domain gets no swatches at all — see
 * `legendModel`, which makes that call once for the card and the app together.
 */
function layoutCardLegend(
  model: LegendModel,
  palette: MapPalette,
  at: { readonly right: number; readonly y: number; readonly size: number },
): { readonly svg: string; readonly left: number } {
  const swatch = at.size * 0.9;
  const parts: string[] = [];
  const labelY = at.y - (swatch - at.size * CAP_HEIGHT_EM) / 2;
  let x = at.right;

  /*
   * Laid out right-to-left, which is not a stylistic choice.
   *
   * There is no text metric here — `approxWidth` charges a fixed advance per
   * character, deliberately on the high side, and the note is the longest string
   * on the card by some way. Walking left-to-right from a start computed as
   * `right - estimatedTotal` accumulates that over-estimate into the *right*
   * edge, which is the edge that shows: the first render of this landed the
   * group 108px short of the margin the lockup and the tagline are flush to.
   *
   * Walking from the right puts the accumulated error on the left instead, where
   * it borders empty space. The rightmost item is then a four-character number
   * whose estimate is off by a few pixels rather than a thirty-six-character
   * sentence whose estimate is off by a hundred.
   *
   * That in turn fixes the order — the caption first, then the range it labels.
   * It reads as a sentence with its units in front, and it is the arrangement
   * that lets the group hang off the same right margin as everything below it.
   */
  const keys = model.keys.slice(0, 6);
  for (let i = keys.length - 1; i >= 0; i--) {
    const { color, label } = keys[i] as LegendKey;
    x -= Math.ceil(approxWidth(label, at.size));
    parts.push(
      text(label, {
        x,
        y: labelY,
        size: at.size,
        fill: palette.faint,
        font: palette.fontSans,
      }),
    );
    x -= 6 + swatch;
    parts.push(
      `<rect x="${num(x)}" y="${num(at.y - swatch)}" width="${num(swatch)}" height="${num(swatch)}" rx="${num(swatch * 0.25)}" fill="${color}"/>`,
    );
    x -= 18;
  }

  /*
   * Anchored `end`, so the one string whose width cannot be estimated well does
   * not have to be. Its right edge is exact and its left edge is wherever the
   * glyphs stop; the returned `left` is the *estimate*, used only to decide
   * whether this collides with the subtitle — over-reserving there errs toward
   * giving the legend its own line, which is the harmless direction.
   */
  parts.push(
    text(model.note, {
      x,
      y: labelY,
      size: at.size,
      fill: palette.faint,
      font: palette.fontSans,
      anchor: "end",
    }),
  );

  return { svg: parts.join(""), left: x - Math.ceil(approxWidth(model.note, at.size)) };
}
